import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Carousel,
  CarouselItem,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "../axios";
import { AppContext } from "../context";
import ProductDetailModal from "../catalog/ProductDetailModal";
import RangeComparisonModal from "./components/RangeComparisonModal";
import StoreCatalogPromoBanner from "./components/StoreCatalogPromoBanner";
import StoreSampleCabinetCta from "./components/StoreSampleCabinetCta";
import StoreDeliveryChargesInfo from "./components/StoreDeliveryChargesInfo";
import { isActiveStoreCustomer } from "./buyerStatus";
import {
  CATALOG_PROMO_TOP_BANNER_INDEX,
  catalogPromoBannerIndexAfterProduct,
  shouldShowCatalogPromoBannerAfterProduct,
} from "./catalogPromoBanners";
import { StoreBuyerQuotePickerItems } from "./components/StoreBuyerQuotePickerItems";
import { StoreQuoteFolderPickerModal } from "./components/StoreQuoteFolderPickerModal";
import PriceMetaComponent from "./components/PriceMetaComponent";
import {
  addStoreProductToCartProject,
  createStoreProject,
  createStoreProjectItem,
  getCatalogProduct,
  getStoreProjectItems,
  deleteStoreProjectItem,
  sortStoreProjectsEarliestFirst,
  sortedStoreProjectsFromApiPayload,
  sortedStoreProjectsForBuyerAccount,
  sortStoreProjectsForBuyerAccount,
  firstManageableProjectIdFromList,
  filterOwnStoreDraftQuotesForAdd,
  filterStoreDraftQuotesSelectable,
  isStoreSampleQuote,
  PENDING_SAMPLE_CABINET_QUOTE_KEY,
  hasPendingSampleCabinetIntent,
  clearPendingSampleCabinetIntent,
  hasPendingPanelSamplePhoneIntent,
  clearPendingPanelSamplePhoneIntent,
  hasPendingQuoteDrawingWhatsAppIntent,
  clearPendingQuoteDrawingWhatsAppIntent,
  setPendingQuoteDrawingWhatsAppIntent,
  executePanelSamplePhoneIntent,
  executeQuoteDrawingWhatsAppIntent,
  createSampleCabinetQuote,
  projectCanManageItems,
  projectRowNeedsManageFlagFromProjectsApi,
  mergeBuyerAccountProjectLists,
  mergeStoreProjectRowsById,
  normalizeStoreUserId,
  isOwnStoreProject,
  MY_FIRST_QUOTE_PROJECT_NAME,
  shouldPromptStoreProjectName,
} from "./service";
import StoreProjectNameModal from "./components/StoreProjectNameModal";
import {
  parseProjectItemsResponseBody,
  flattenProjectItemsTree,
} from "./projectItemsTree";
import {
  walkExistingFolderPathInItems,
  normalizeStoreProjectFolderPath,
} from "./projectFolderPath";
import "./products.css";
import cabinetsImg from "../assets/img/store/cabinets.webp";
import {
  toNullableNumber,
  useStableNewVariantAxes,
  pickAxisProductId,
  pickVariantSizeProductIds,
  resolveVariantSizeAxes,
  StoreProductVariantDimensionPickers,
} from "./variantAxes";
import {
  trackZbAddToCart,
  trackZbInitiateCheckout,
  trackZbListingView,
} from "../common/analytics";
import { getDeliveryTextForSelectedVariant } from "./pages/storeProjectDetail/cartUtils";

const USE_STORE_PRODUCTS_STUB = false;
const STORE_PRODUCTS_SORT_NAME_ASC = "name_asc";

function resolveStoreCatalogListProductId(p) {
  return toNullableNumber(p?.id);
}

function resolveStoreCatalogProductCode(p) {
  const raw = p?.code;

  if (raw == null) return null;

  const code = String(raw).trim();

  return code.length > 0 ? code : null;
}

function codeToUrlCode(code) {
  return String(code ?? "")
    .trim()
    .replaceAll("/", "_");
}

function urlCodeToCode(urlCode) {
  return String(urlCode ?? "")
    .trim()
    .replaceAll("_", "/");
}

/** React list key: `id`, else `cp_id`, else index. */
function reactKeyForStoreCatalogProduct(p, index) {
  const sid = resolveStoreCatalogListProductId(p);
  if (sid != null) return `sp-${sid}`;
  if (p?.cp_id != null && String(p.cp_id) !== "")
    return `cp-${String(p.cp_id)}`;

  return `row-${index}`;
}

function resolveStoreProjectEntityId(entity) {
  return toNullableNumber(entity?.id);
}

/** Store product id on a project line item. */
function resolveProjectLineStoreProductId(it) {
  return toNullableNumber(it?.product_id);
}

function parseStoreProductsPayload(body) {
  const root = body?.data != null ? body.data : body;
  let folders = [];
  let highlight_folders = [];

  if (root && typeof root === "object" && !Array.isArray(root)) {
    if (Array.isArray(root.folders)) {
      folders = root.folders;
    } else if (Array.isArray(root.products) && root.products.length > 0) {
      folders = [
        {
          name: "Results",
          identifier: "__store_products_flat__",
          products: root.products,
          children: [],
        },
      ];
    }
    if (Array.isArray(root.highlight_folders)) {
      highlight_folders = root.highlight_folders;
    }
  } else if (
    Array.isArray(root) &&
    root.length > 0 &&
    (Array.isArray(root[0]?.products) || Array.isArray(root[0]?.children))
  ) {
    folders = root;
  }

  const meta =
    root && typeof root === "object" && !Array.isArray(root) ? root : {};
  const total_catalog_products = meta.total_catalog_products ?? null;
  const total_entries = meta.total_entries ?? null;
  const has_projects = meta.has_projects ?? null;

  return {
    folders,
    highlight_folders,
    total_catalog_products,
    total_entries,
    has_projects,
  };
}

function parseStoreProjectsFromPayload(body, currentUser) {
  if (currentUser) {
    return sortedStoreProjectsForBuyerAccount(body, currentUser);
  }
  return sortedStoreProjectsFromApiPayload(body);
}

function storeCatalogProductMatchesSearchTerm(p, qLower) {
  if (!qLower) return true;
  const sp = getSizeParts(p?.size);
  const sizeHay = [sp.height, sp.width, sp.depth]
    .map((x) => normalizeDimensionToken(x).toLowerCase())
    .filter(Boolean)
    .join(" ");
  const hay = [p?.name, p?.code, sizeHay, p?.range, p?.description, p?.cp_code]
    .map((x) => String(x ?? "").toLowerCase())
    .join(" ");
  return hay.includes(qLower);
}

/** Client-side search when `USE_STORE_PRODUCTS_STUB` is on (mirrors API search loosely). */
function filterStoreCatalogFoldersBySearchTerm(folders, query) {
  const qLower = String(query ?? "")
    .trim()
    .toLowerCase();
  if (!qLower) return folders;
  if (!Array.isArray(folders)) return [];
  return folders
    .map((folder) => {
      const products = Array.isArray(folder?.products) ? folder.products : [];
      const children = Array.isArray(folder?.children) ? folder.children : [];
      const filteredProducts = products.filter((p) =>
        storeCatalogProductMatchesSearchTerm(p, qLower)
      );
      const filteredChildren = filterStoreCatalogFoldersBySearchTerm(
        children,
        query
      );
      if (filteredProducts.length === 0 && filteredChildren.length === 0)
        return null;
      return {
        ...folder,
        products: filteredProducts,
        children: filteredChildren,
      };
    })
    .filter(Boolean);
}

async function loadStoreProductsResponseBody() {
  if (USE_STORE_PRODUCTS_STUB) {
    const mod = await import("../catalog/stubs/products.json");
    return mod.default;
  }
  const response = await axios.get("/store/products", {
    params: { sort: STORE_PRODUCTS_SORT_NAME_ASC },
  });
  return response?.data ?? response;
}

async function loadStoreProductsSearchResponseBody(searchTerm) {
  if (USE_STORE_PRODUCTS_STUB) {
    const mod = await import("../catalog/stubs/products.json");
    const parsed = parseStoreProductsPayload(mod.default);
    const folders = filterStoreCatalogFoldersBySearchTerm(
      parsed.folders,
      searchTerm
    );
    return {
      data: {
        folders,
        highlight_folders: parsed.highlight_folders,
        total_catalog_products: parsed.total_catalog_products,
        total_entries: parsed.total_entries,
        has_projects: parsed.has_projects,
      },
    };
  }
  const response = await axios.get("/store/products", {
    params: { search_term: searchTerm, sort: STORE_PRODUCTS_SORT_NAME_ASC },
  });
  return response?.data ?? response;
}

function countProductsInFolderSubtree(folder) {
  const products = Array.isArray(folder?.products) ? folder.products : [];
  const children = Array.isArray(folder?.children) ? folder.children : [];
  let n = products.length;
  for (const c of children) n += countProductsInFolderSubtree(c);
  return n;
}

function normalizePathPart(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normalizeFolderQueryToken(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase()
    .replace(/%20/g, " ")
    .replace(/[\s-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function getFolderNodeKey(parentPath, folder, indexInSiblings) {
  const segment =
    folder?.identifier != null && String(folder.identifier) !== ""
      ? String(folder.identifier)
      : `${String(folder?.name ?? "").trim() || "folder"}~${indexInSiblings}`;
  return parentPath.length ? `${parentPath.join("/")}/${segment}` : segment;
}

function findFolderNodeMatchByQuery(
  folders,
  folderQuery,
  parentPath = [],
  ancestorNodeKeys = []
) {
  const target = normalizeFolderQueryToken(folderQuery);
  if (!target || !Array.isArray(folders)) return null;

  for (let index = 0; index < folders.length; index += 1) {
    const folder = folders[index];
    const nodeKey = getFolderNodeKey(parentPath, folder, index);
    const identifier = normalizeFolderQueryToken(folder?.identifier);
    const byName = normalizeFolderQueryToken(folder?.name);
    if (identifier === target || byName === target) {
      return { nodeKey, ancestorNodeKeys };
    }

    const segment = String(folder?.name ?? "").trim();
    const nextPath = segment ? [...parentPath, segment] : parentPath;
    const children = Array.isArray(folder?.children) ? folder.children : [];
    const nestedMatch = findFolderNodeMatchByQuery(children, target, nextPath, [
      ...ancestorNodeKeys,
      nodeKey,
    ]);
    if (nestedMatch) return nestedMatch;
  }
  return null;
}

function getApiErrorMessage(errorLike, fallback) {
  const message =
    errorLike?.error ||
    errorLike?.response?.data?.error ||
    errorLike?.response?.data?.message ||
    errorLike?.message;
  const normalized = String(message ?? "").trim();
  return normalized || fallback;
}

function collectAllFolderNodeKeys(folders, parentPath = []) {
  if (!Array.isArray(folders) || folders.length === 0) return [];
  const keys = [];
  for (let index = 0; index < folders.length; index += 1) {
    const folder = folders[index];
    keys.push(getFolderNodeKey(parentPath, folder, index));
    const segment = String(folder?.name ?? "").trim();
    const nextPath = segment ? [...parentPath, segment] : parentPath;
    const children = Array.isArray(folder?.children) ? folder.children : [];
    keys.push(...collectAllFolderNodeKeys(children, nextPath));
  }
  return keys;
}

function collectAddedCountsFromFolders(folders, out = {}) {
  if (!Array.isArray(folders)) return out;
  for (const folder of folders) {
    const products = Array.isArray(folder?.products) ? folder.products : [];
    for (const p of products) {
      const pid = resolveStoreCatalogListProductId(p);
      if (pid == null) continue;
      const n = Number(p?.added ?? 0);
      out[pid] = Number.isNaN(n) ? 0 : n;
    }
    collectAddedCountsFromFolders(folder?.children || [], out);
  }
  return out;
}

function folderDomId(nodeKey) {
  return `store-folder-${encodeURIComponent(String(nodeKey ?? ""))}`;
}

/** First gallery URL; `images` is `string[]`. */
function getPrimaryCatalogProductImageUrl(p) {
  if (!Array.isArray(p?.images) || p.images.length === 0) return null;
  const first = p.images[0];
  if (typeof first !== "string") return null;
  const s = first.trim();
  return s || null;
}

function toPriceNumber(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

function formatInrAmount(n) {
  if (n == null) return null;
  return `₹${n.toLocaleString("en-IN")}`;
}

/** List (`price`) vs sale (`discount_price`) when both present and differ. */
function getProductPriceDisplay(p) {
  const listNum = toPriceNumber(p?.price);
  const discNum = toPriceNumber(p?.discount_price);
  if (discNum != null && listNum != null && discNum !== listNum) {
    return {
      listText: formatInrAmount(listNum),
      saleText: formatInrAmount(discNum),
    };
  }
  if (discNum != null) {
    return { listText: null, saleText: formatInrAmount(discNum) };
  }
  if (listNum != null) {
    return { listText: null, saleText: formatInrAmount(listNum) };
  }
  return { listText: null, saleText: null };
}

function formatSqftPrice(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;

  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} / sqft`;
}

/** Single H/W/D token from API `size` strings (trim, NBSP → space). */
function normalizeDimensionToken(s) {
  return String(s ?? "")
    .replace(/\u00a0/g, " ")
    .trim();
}

function getSizeParts(sizeValue) {
  if (sizeValue && typeof sizeValue === "object" && !Array.isArray(sizeValue)) {
    const h = sizeValue.height;
    const w = sizeValue.width;
    const d = sizeValue.depth;
    if (!Array.isArray(h) && !Array.isArray(w) && !Array.isArray(d)) {
      if (h !== undefined || w !== undefined || d !== undefined) {
        return {
          height: normalizeDimensionToken(h),
          width: normalizeDimensionToken(w),
          depth: normalizeDimensionToken(d),
        };
      }
    }
  }
  const raw = normalizeDimensionToken(sizeValue);
  if (!raw) return { height: "", width: "", depth: "" };
  const parts = raw
    .split(/[x×]/i)
    .map((s) => normalizeDimensionToken(s))
    .filter(Boolean);
  return {
    height: parts[0] ?? "",
    width: parts[1] ?? "",
    depth: parts[2] ?? "",
  };
}

function unwrapStoreCatalogProductShow(res) {
  const body = res?.data ?? res;
  const data = body?.data ?? body;
  if (!data || (!data.id && !data.product_id)) return null;
  return data;
}

/**
 * Replace the first folder row whose store product id matches `oldProductId`
 * (immutable). `folderPath` is the path of folder *names* for this row (same as list props).
 */
function replaceStoreCatalogProductInFolders(
  folders,
  folderPath,
  oldProductId,
  newProduct
) {
  const oldId = toNullableNumber(oldProductId);
  if (oldId == null || !Array.isArray(folders)) return folders;
  const parts = (folderPath || []).map(normalizePathPart);

  function walk(nodes, depth) {
    if (!Array.isArray(nodes)) return nodes;
    return nodes.map((folder) => {
      const segment = String(folder?.name ?? "").trim();
      if (
        depth >= parts.length ||
        normalizePathPart(segment) !== parts[depth]
      ) {
        const ch = folder?.children;
        if (Array.isArray(ch) && ch.length > 0) {
          return { ...folder, children: walk(ch, depth) };
        }
        return folder;
      }
      if (depth === parts.length - 1) {
        const products = Array.isArray(folder?.products) ? folder.products : [];
        let replaced = false;
        const nextProducts = products.map((row) => {
          if (replaced) return row;
          if (resolveStoreCatalogListProductId(row) === oldId) {
            replaced = true;
            return {
              ...newProduct,
              folder_path: Array.isArray(newProduct?.folder_path)
                ? newProduct.folder_path
                : folderPath,
            };
          }
          return row;
        });
        return { ...folder, products: nextProducts };
      }
      return {
        ...folder,
        children: walk(folder.children || [], depth + 1),
      };
    });
  }

  return walk(folders, 0);
}

function folderTreeContainsStoreProductId(nodes, productId) {
  const pid = toNullableNumber(productId);
  if (pid == null || !Array.isArray(nodes)) return false;
  for (const folder of nodes) {
    const products = Array.isArray(folder?.products) ? folder.products : [];
    for (const p of products) {
      if (resolveStoreCatalogListProductId(p) === pid) return true;
    }
    const children = Array.isArray(folder?.children) ? folder.children : [];
    if (folderTreeContainsStoreProductId(children, pid)) return true;
  }
  return false;
}

/** When folder path does not match (e.g. search vs full-tree layout), replace by id anywhere. */
function replaceStoreCatalogProductAnywhereInFolders(
  folders,
  oldProductId,
  newProduct
) {
  const oldId = toNullableNumber(oldProductId);
  if (oldId == null || !Array.isArray(folders)) return folders;

  function walk(nodeList) {
    if (!Array.isArray(nodeList)) return nodeList;
    let changed = false;
    const out = nodeList.map((folder) => {
      const products = Array.isArray(folder?.products) ? folder.products : [];
      const children = Array.isArray(folder?.children) ? folder.children : [];
      let rowReplaced = false;
      const nextProducts = products.map((row) => {
        if (rowReplaced) return row;
        if (resolveStoreCatalogListProductId(row) === oldId) {
          rowReplaced = true;
          changed = true;
          return {
            ...newProduct,
            folder_path: Array.isArray(row?.folder_path)
              ? row.folder_path
              : newProduct.folder_path,
          };
        }
        return row;
      });
      if (rowReplaced) {
        return { ...folder, products: nextProducts, children };
      }
      const nextChildren = walk(children);
      if (nextChildren !== children) {
        changed = true;
        return { ...folder, products, children: nextChildren };
      }
      return folder;
    });
    return changed ? out : nodeList;
  }

  return walk(folders);
}

function replaceStoreCatalogProductInFoldersSmart(
  folders,
  folderPath,
  oldProductId,
  newProduct
) {
  const afterPath = replaceStoreCatalogProductInFolders(
    folders,
    folderPath,
    oldProductId,
    newProduct
  );
  const oldId = toNullableNumber(oldProductId);
  if (oldId == null) return afterPath;
  return folderTreeContainsStoreProductId(afterPath, oldId)
    ? replaceStoreCatalogProductAnywhereInFolders(afterPath, oldId, newProduct)
    : afterPath;
}

function trimCatalogString(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}

function StoreProductRowActionPane({
  effectiveProduct,
  folderPath,
  productId,
  isBusy,
  addedCount,
  isNonCartUser,
  isCustomerUser,
  isDealerUser,
  isAdminUser,
  currentUser,
  projectsList,
  onAddCart,
  onAddProject,
  onCreateProjectAndAdd,
  onAuthRequired,
  suppressQuoteDropdown = false,
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const prevBusyRef = useRef(false);

  useEffect(() => {
    if (suppressQuoteDropdown) {
      setDropdownOpen(false);
    }
  }, [suppressQuoteDropdown]);

  useEffect(() => {
    if (prevBusyRef.current && !isBusy) {
      setDropdownOpen(false);
    }
    prevBusyRef.current = isBusy;
  }, [isBusy]);

  if (productId == null) {
    return <span className="d-inline-block text-muted small">—</span>;
  }

  if (isDealerUser || isAdminUser) {
    return null;
  }

  /** Cart / quote flow (guest / quick add). */
  const isRowLoggedIn =
    currentUser && Boolean(currentUser?.mobile) && !currentUser?.temp;
  const cartAddLabel = isRowLoggedIn ? "Add to Quote" : "View price";

  /** Logged-in (non-guest) users pick a quote; guests use cart add below. */
  if (isCustomerUser || isNonCartUser) {
    const draftQuotes = filterOwnStoreDraftQuotesForAdd(
      projectsList,
      currentUser
    );
    const quoteCount = draftQuotes.length;

    const chooseQuoteMenu =
      quoteCount >= 1 ? (
        <div className="d-inline-block" onClick={(e) => e.stopPropagation()}>
          <Dropdown
            isOpen={dropdownOpen}
            toggle={() => setDropdownOpen((prev) => !prev)}
            className="position-relative"
            direction="up"
          >
            <DropdownToggle
              color="primary"
              size="sm"
              className="w-auto fw-bold text-nowrap px-3 rounded"
              caret={false}
              disabled={isBusy}
            >
              {cartAddLabel}
            </DropdownToggle>
            <DropdownMenu
              end
              flip={false}
              strategy="fixed"
              container="body"
              className="shadow rounded-2 store-products__quote-dropdown-menu"
              style={{ maxWidth: "min(280px, 85vw)" }}
            >
              <StoreBuyerQuotePickerItems
                quotes={draftQuotes}
                currentUser={currentUser}
                placement="menu"
                renderItem={(proj, displayName) => {
                  const pid = proj?.id ?? proj?.project_id;
                  const canManage = projectCanManageItems(proj);
                  return (
                    <DropdownItem
                      key={String(pid)}
                      toggle={false}
                      disabled={!canManage}
                      title={
                        canManage
                          ? undefined
                          : "You can open this project but only the owner can add products."
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!canManage) return;
                        setDropdownOpen(false);
                        onAddProject?.(
                          pid,
                          displayName,
                          effectiveProduct,
                          folderPath
                        );
                      }}
                    >
                      {displayName}
                    </DropdownItem>
                  );
                }}
              />
              <DropdownItem divider toggle={false} />
              <DropdownItem
                toggle={false}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  window.setTimeout(() => {
                    onCreateProjectAndAdd?.(effectiveProduct, folderPath);
                  }, 0);
                }}
              >
                Add new quote
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      ) : (
        <div
          className="d-inline-block"
          role="presentation"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="btn btn-primary btn-sm fw-bold text-nowrap px-3"
            disabled={isBusy}
            onClick={(e) => {
              e.stopPropagation();
              onCreateProjectAndAdd?.(effectiveProduct, folderPath);
            }}
          >
            {cartAddLabel}
          </button>
        </div>
      );

    return chooseQuoteMenu;
  }

  return (
    <button
      type="button"
      className="btn btn-primary btn-sm fw-bold text-nowrap px-3"
      disabled={isBusy}
      onClick={(e) => {
        e.stopPropagation();

        if (!isRowLoggedIn) {
          onAuthRequired?.();
          return;
        }

        onAddCart?.(effectiveProduct, folderPath);
      }}
    >
      {isBusy ? "..." : cartAddLabel}
    </button>
  );
}

function StoreProductCode({ codeText, onCopyCode }) {
  if (!codeText) return null;
  return (
    <div className="store-products-row__sku store-products-row__sku--under-name store-products-row__sku-inline">
      <span>{codeText}</span>
      <button
        type="button"
        className="store-products-row__sku-copy-btn"
        aria-label={`Copy code ${codeText}`}
        title="Copy code"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onCopyCode?.(codeText);
        }}
      >
        <span aria-hidden>⧉</span>
      </button>
    </div>
  );
}

function StoreProductRow({
  product: p,
  onOpenProduct,
  onAddCart,
  onAddProject,
  onCreateProjectAndAdd,
  onLoadVariantProduct,
  folderPath,
  isBusy,
  addedCount = 0,
  canShowPrice = true,
  isNonCartUser = false,
  isCustomerUser = false,
  projectsList = [],
  onSkuCopied,
  onAuthRequired,
  addedFlashText = "",
  isDealerUser = false,
  isAdminUser = false,
  currentUser = null,
  suppressQuoteDropdown = false,
}) {
  const axes = useStableNewVariantAxes(p);
  const listProductId = resolveStoreCatalogListProductId(p);
  const {
    heightOpts,
    widthOpts,
    depthOpts,
  } = resolveVariantSizeAxes(axes);
  const { heightPid, widthPid, depthPid } =
    pickVariantSizeProductIds(axes, listProductId);
  const rangeOpts = axes?.range ?? [];
  const rangePid = pickAxisProductId(rangeOpts, listProductId);
  const [isRangeHelpOpen, setIsRangeHelpOpen] = useState(false);
  const [isPriceMetaModalOpen, setIsPriceMetaModalOpen] = useState(false);

  const nameRaw = p?.name;
  const name =
    nameRaw != null && String(nameRaw).trim() !== ""
      ? String(nameRaw).trim()
      : "Product";
  const { listText: priceListText, saleText: priceSaleText } =
    getProductPriceDisplay(p);
  const sqftPriceText = formatSqftPrice(p?.sqft_price);
  const deliveryText = getDeliveryTextForSelectedVariant({
    currentProduct: p,
    heightOpts,
    widthOpts,
    depthOpts,
    heightPid,
    widthPid,
    depthPid,
    useTomorrowForStandard: true,
  });
  const productId = listProductId;
  const primaryImage = getPrimaryCatalogProductImageUrl(p);
  const codeText = trimCatalogString(p?.code);
  const hasPriceAmount = Boolean(priceListText || priceSaleText);
  const priceMeta = p?.price_meta;
  const hasDisplayablePrice = Boolean(
    priceListText || priceSaleText || (canShowPrice && sqftPriceText)
  );
  const showAdminPriceMetaIcon = isAdminUser && hasDisplayablePrice;
  const hasSaleOrSqftRow = Boolean(
    priceSaleText || (canShowPrice && sqftPriceText)
  );

  const adminPriceMetaTrigger = showAdminPriceMetaIcon ? (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none"
      aria-label="View price calculation details"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsPriceMetaModalOpen(true);
      }}
    >
      <i className="fa-solid fa-circle-info" aria-hidden />
    </button>
  ) : null;

  const handlePid = (nextPid) => {
    const nid = toNullableNumber(nextPid);
    if (nid == null || nid === listProductId) return;
    onLoadVariantProduct?.(folderPath, p, nid);
  };

  const rangePillOpts = rangeOpts;

  return (
    <>
      <div
        className="store-products-row"
        role="button"
        tabIndex={0}
        onClick={() => {
          if (productId != null) onOpenProduct?.(p);
        }}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && productId != null) {
            e.preventDefault();
            onOpenProduct?.(p);
          }
        }}
        aria-label={productId != null ? `Open ${name}` : undefined}
      >
        <div className="store-products-row__media-col">
          <div
            className="store-products-row__image-wrap d-flex justify-content-center rounded-2 p-2"
            style={{
              background: "linear-gradient(180deg, #FAF8F3 0%, #E6E0D5 100%)",
              border: "1px solid #e3dccf",
            }}
          >
            {primaryImage ? (
              <img
                className="store-products-row__img"
                src={primaryImage}
                alt=""
              />
            ) : (
              <div className="store-products-row__img-placeholder">
                No image
              </div>
            )}
          </div>
        </div>
        <div className="store-products-row__detail-col">
          <div className="store-products-row__detail-stack">
            <div className="store-products-row__name">{name}</div>
            <StoreProductCode codeText={codeText} onCopyCode={onSkuCopied} />
            <div
              className="store-products-row__controls"
              onClick={(e) => e.stopPropagation()}
            >
              <StoreProductVariantDimensionPickers
                axes={axes}
                listProductId={listProductId}
                onPickProductId={handlePid}
                keyPrefix="list"
              />
              {rangePillOpts.length > 0 ? (
                <div className="mt-2">
                  <div
                    className="store-products-row__range-pills"
                    role="group"
                    aria-label="Range"
                  >
                    {rangePillOpts.map((o) => {
                      const pid = toNullableNumber(o?.product_id);
                      const active =
                        pid != null && rangePid != null && pid === rangePid;
                      return (
                        <button
                          key={`r-${pid}`}
                          type="button"
                          className={
                            active
                              ? "store-products-row__range-pill store-products-row__range-pill--active"
                              : "store-products-row__range-pill"
                          }
                          onClick={() => pid != null && handlePid(pid)}
                        >
                          {String(o.label ?? "").trim()}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      className="btn btn-link p-0 text-secondary text-decoration-none store-products-row__range-help"
                      aria-label="Open range comparison image"
                      onClick={() => setIsRangeHelpOpen(true)}
                    >
                      <i
                        className="fa-regular fa-circle-question fs-6"
                        aria-hidden="true"
                      />
                    </button>
                    <RangeComparisonModal
                      isOpen={isRangeHelpOpen}
                      toggle={() => setIsRangeHelpOpen(false)}
                    />
                  </div>
                </div>
              ) : null}
              <div className="mt-1">
                <button
                  type="button"
                  className="btn btn-link p-0 small text-decoration-none"
                  onClick={() => {
                    if (productId != null) onOpenProduct?.(p);
                  }}
                >
                  more details &gt;&gt;
                </button>
              </div>
              <div
                className="store-products-row__price-actions"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="store-products-row__price-pane">
                  {canShowPrice && (
                    <>
                      {priceListText ? (
                        <div className="store-products-row__price-row d-flex align-items-center gap-2 flex-wrap">
                          <span className="store-products-row__price store-products-row__price--list">
                            MRP {priceListText}
                          </span>
                          {adminPriceMetaTrigger}
                        </div>
                      ) : null}
                      {priceSaleText || (canShowPrice && sqftPriceText) ? (
                        <div className="store-products-row__price-row d-flex align-items-baseline gap-2 flex-wrap">
                          <span className="d-inline-flex align-items-baseline gap-2 flex-wrap">
                            {priceSaleText ? (
                              <span className="store-products-row__price fw-bold text-dark">
                                {priceSaleText}
                              </span>
                            ) : null}
                            <small className="text-muted fw-normal">
                              incl. GST
                            </small>
                            {canShowPrice && sqftPriceText ? (
                              <small className="text-primary fw-normal fw-bold">
                                ({sqftPriceText.replace("/ sqft", "/ sq.ft.")})
                              </small>
                            ) : null}
                          </span>
                          {!priceListText ? adminPriceMetaTrigger : null}
                        </div>
                      ) : null}
                      {canShowPrice && !hasPriceAmount ? (
                        <div className="store-products-row__price-row d-flex align-items-center gap-2">
                          <span className="store-products-row__price store-products-row__price--empty">
                            —
                          </span>
                          {!priceListText && !hasSaleOrSqftRow
                            ? adminPriceMetaTrigger
                            : null}
                        </div>
                      ) : null}
                    </>
                  )}
                  <div
                    className="text-success mt-1 d-flex align-items-center gap-1 fw-bold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    <i className="fa-solid fa-truck" aria-hidden />
                    Shipping <span>{deliveryText}</span>
                  </div>
                </div>
                <div className="store-products-row__action-with-notice">
                  <div className="store-products-row__action-pane">
                    <StoreProductRowActionPane
                      effectiveProduct={p}
                      folderPath={folderPath}
                      productId={productId}
                      isBusy={isBusy}
                      addedCount={addedCount}
                      isNonCartUser={isNonCartUser}
                      isCustomerUser={isCustomerUser}
                      isDealerUser={isDealerUser}
                      isAdminUser={isAdminUser}
                      currentUser={currentUser}
                      projectsList={projectsList}
                      onAddCart={onAddCart}
                      onAddProject={onAddProject}
                      onCreateProjectAndAdd={onCreateProjectAndAdd}
                      onAuthRequired={onAuthRequired}
                      suppressQuoteDropdown={suppressQuoteDropdown}
                    />
                  </div>
                  {addedFlashText ? (
                    <div
                      className="store-products-row__inline-added-flash"
                      role="status"
                      aria-live="polite"
                    >
                      {addedFlashText}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PriceMetaComponent
        isOpen={isPriceMetaModalOpen}
        toggle={() => setIsPriceMetaModalOpen(false)}
        priceMeta={priceMeta}
        productName={name}
      />
    </>
  );
}

const BANNER_DEFINITIONS = [
  {
    id: "sample_cabinet",
    image: "/store/home/sample_cabinet.webp",
    tabText: "Sample Cabinet",
    defaultActionText: "Order Sample Cabinet",
  },
  {
    id: "free_shipping",
    image: "/store/home/free_shipping.webp",
    tabText: "FREE Delivery",
    defaultActionText: "View Charges",
  },
  {
    id: "buy1-get1",
    image: "/store/home/buy_1_get_1.jpg",
    tabText: "Buy 1 Get 1",
  },
  {
    id: "minifix-discount",
    image: "/store/home/minifix_discount.jpg",
    tabText: "Minfix Discount",
  },
  {
    id: "25k-offer",
    image: "/store/home/month_end.jpg",
    tabText: "25K Offer",
  },
  {
    id: "semi-modular",
    image: "/store/home/semi_modular.jpg",
    tabText: "Semi Modular",
  },
];

const STORE_HIGHLIGHTS = [
  {
    icon: "fa-solid fa-cube",
    title: "Carcass Only",
    subText: "All carcass panels excluding shutters",
  },
  {
    icon: "fa-solid fa-indian-rupee-sign",
    title: "Cash On Delivery",
    subText: "Just order & pay right on delivery",
  },
  {
    icon: "fa-solid fa-hammer",
    title: "Hardwares",
    subText: "All accessories & hardwares included",
  },
  {
    icon: "fa-solid fa-truck",
    title: "FREE Delivery",
    subText: "Anywhere in India, for orders above ₹50,000",
  },
];

function StoreProductsLoadingSkeleton({ rows = 6 }) {
  const items = Array.from({ length: rows }, (_, i) => i);
  return (
    <div className="store-products-skeleton" aria-hidden="true">
      {items.map((idx) => (
        <div
          key={`skeleton-row-${idx}`}
          className="store-products-row store-products-row--skeleton"
        >
          <div className="store-products-row__media-col">
            <div className="store-products-skeleton__block store-products-skeleton__image" />
          </div>
          <div className="store-products-row__detail-col">
            <div className="store-products-row__detail-stack">
              <div className="store-products-skeleton__block store-products-skeleton__title" />
              <div className="store-products-skeleton__block store-products-skeleton__sku" />
              <div className="store-products-skeleton__dims-row">
                <div className="store-products-skeleton__block store-products-skeleton__dim" />
                <div className="store-products-skeleton__block store-products-skeleton__dim" />
                <div className="store-products-skeleton__block store-products-skeleton__dim" />
              </div>
              <div className="store-products-skeleton__block store-products-skeleton__price" />
              <div className="store-products-skeleton__block store-products-skeleton__delivery" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function resolveBannerIndex(bannerList, bannerId) {
  if (!Array.isArray(bannerList) || bannerList.length === 0) return 0;
  if (bannerId == null) return 0;

  const index = bannerList.findIndex((banner) => banner.id === bannerId);

  return index >= 0 ? index : 0;
}

const BANNER_AUTOPLAY_MS = 5000;
const BANNER_TABS_VISIBLE = 2;

function StoreBannerSlideAction({ banner }) {
  if (!banner.actionText) return null;

  const wrapperClassName = "position-absolute pe-2";
  const wrapperStyle = { bottom: "3.5rem", right: "3rem" };
  const buttonClassName =
    "btn bg-black border border-black text-white px-3 py-2";
  const buttonStyle = { fontSize: "1.09375rem" };

  if (banner.actionHref) {
    return (
      <div className={wrapperClassName} style={wrapperStyle}>
        <Link
          to={banner.actionHref}
          className={buttonClassName}
          style={buttonStyle}
          onClick={(e) => e.stopPropagation()}
        >
          {banner.actionText}
        </Link>
      </div>
    );
  }

  if (!banner.actionOnClick) return null;

  return (
    <div className={wrapperClassName} style={wrapperStyle}>
      <button
        type="button"
        className={buttonClassName}
        style={buttonStyle}
        onClick={(e) => {
          e.stopPropagation();
          banner.actionOnClick();
        }}
      >
        {banner.actionText}
      </button>
    </div>
  );
}

function StoreBannersCarousel({
  banners: bannerList,
  defaultBannerId,
  disableAutoplay = false,
}) {
  const hasBanners = Array.isArray(bannerList) && bannerList.length > 0;
  const [activeIndex, setActiveIndex] = useState(() =>
    resolveBannerIndex(bannerList, defaultBannerId)
  );
  const [tabWindowStart, setTabWindowStart] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [autoplayEpoch, setAutoplayEpoch] = useState(0);
  const animatingRef = useRef(false);
  const touchStartXRef = useRef(null);

  const maxTabWindowStart = Math.max(
    0,
    bannerList.length - BANNER_TABS_VISIBLE
  );
  const showTabChevrons = bannerList.length > BANNER_TABS_VISIBLE;
  const canScrollTabsLeft = tabWindowStart > 0;
  const canScrollTabsRight = tabWindowStart < maxTabWindowStart;
  const visibleBanners = showTabChevrons
    ? bannerList.slice(tabWindowStart, tabWindowStart + BANNER_TABS_VISIBLE)
    : bannerList;

  useEffect(() => {
    animatingRef.current = animating;
  }, [animating]);

  useEffect(() => {
    if (!hasBanners) return;

    setActiveIndex(resolveBannerIndex(bannerList, defaultBannerId));
    setAutoplayEpoch((epoch) => epoch + 1);
  }, [bannerList, defaultBannerId, hasBanners]);

  useEffect(() => {
    if (!hasBanners || bannerList.length <= 1 || disableAutoplay) return;

    const timer = window.setInterval(() => {
      if (animatingRef.current) return;
      setActiveIndex((prev) => (prev === bannerList.length - 1 ? 0 : prev + 1));
    }, BANNER_AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [hasBanners, bannerList.length, autoplayEpoch, disableAutoplay]);

  useEffect(() => {
    if (!hasBanners || bannerList.length <= BANNER_TABS_VISIBLE) {
      setTabWindowStart(0);
      return;
    }

    const maxStart = bannerList.length - BANNER_TABS_VISIBLE;

    setTabWindowStart((start) => {
      const windowEnd = start + BANNER_TABS_VISIBLE - 1;
      if (activeIndex >= start && activeIndex <= windowEnd) return start;

      return Math.min(Math.max(0, activeIndex - 1), maxStart);
    });
  }, [activeIndex, bannerList.length, hasBanners]);

  const scrollTabsBy = (delta) => {
    const nextStart = Math.min(
      maxTabWindowStart,
      Math.max(0, tabWindowStart + delta)
    );
    if (nextStart === tabWindowStart) return;

    setTabWindowStart(nextStart);

    const windowEnd = nextStart + BANNER_TABS_VISIBLE - 1;
    if (activeIndex < nextStart || activeIndex > windowEnd) {
      setActiveIndex(nextStart);
      bumpAutoplayEpoch();
    }
  };

  const scrollTabsLeft = () => {
    scrollTabsBy(-1);
  };

  const scrollTabsRight = () => {
    scrollTabsBy(1);
  };

  const bumpAutoplayEpoch = () => {
    setAutoplayEpoch((epoch) => epoch + 1);
  };

  const goToIndex = (newIndex) => {
    if (animating) return;

    setActiveIndex(newIndex);
    bumpAutoplayEpoch();
  };

  const next = () => {
    if (animating || !hasBanners) return;

    setActiveIndex((prev) => (prev === bannerList.length - 1 ? 0 : prev + 1));
    bumpAutoplayEpoch();
  };

  const previous = () => {
    if (animating || !hasBanners) return;

    setActiveIndex((prev) => (prev === 0 ? bannerList.length - 1 : prev - 1));
    bumpAutoplayEpoch();
  };

  const handleCarouselTouchStart = (e) => {
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };

  const handleCarouselTouchEnd = (e) => {
    if (bannerList.length <= 1) return;

    const startX = touchStartXRef.current;
    const endX = e.changedTouches?.[0]?.clientX ?? null;
    touchStartXRef.current = null;
    if (startX == null || endX == null) return;

    const deltaX = endX - startX;
    if (Math.abs(deltaX) < 30) return;

    // Swipe left -> previous, swipe right -> next (matches ProductDetailModal).
    if (deltaX < 0) {
      previous();
    } else {
      next();
    }
  };

  if (!hasBanners) return null;

  return (
    <div
      className="w-100 mw-100 d-flex flex-column position-relative"
      style={{ maxHeight: "45vh", touchAction: "pan-y pinch-zoom" }}
      aria-label="Product range banners"
      onTouchStart={handleCarouselTouchStart}
      onTouchEnd={handleCarouselTouchEnd}
    >
      <Carousel
        activeIndex={activeIndex}
        next={next}
        previous={previous}
        interval={false}
        className="w-100 store-banners-carousel__slides flex-grow-1 min-h-0"
      >
        {bannerList.map((banner) => (
          <CarouselItem
            key={banner.id}
            onExiting={() => setAnimating(true)}
            onExited={() => setAnimating(false)}
          >
            <div className="position-relative h-100 w-100">
              <img
                src={banner.image}
                alt={banner.tabText}
                className="w-100 object-fit-cover d-block"
              />
              <StoreBannerSlideAction banner={banner} />
            </div>
          </CarouselItem>
        ))}
      </Carousel>
      <div
        className="d-flex flex-row flex-nowrap align-items-stretch justify-content-start gap-1 w-100 mw-100 px-2 flex-shrink-0 position-absolute bottom-0 pe-none"
        style={{ background: "rgba(255,255,255,0.5)" }}
      >
        {showTabChevrons ? (
          <button
            type="button"
            className="btn btn-link btn-sm d-flex align-items-center flex-shrink-0 text-black pe-auto px-1"
            onClick={scrollTabsLeft}
            disabled={!canScrollTabsLeft}
            aria-label="Previous banner tabs"
          >
            <i className="fa-solid fa-chevron-left" aria-hidden />
          </button>
        ) : null}
        <div
          className="d-flex flex-row flex-nowrap flex-grow-1 min-w-0"
          role="tablist"
          aria-label="Banner tabs"
        >
          {visibleBanners.map((banner, visibleOffset) => {
            const index = showTabChevrons
              ? tabWindowStart + visibleOffset
              : visibleOffset;
            const isActive = index === activeIndex;

            return (
              <button
                key={banner.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`btn btn-sm flex-fill min-w-0 small text-black radius-0 text-center text-uppercase fw-bold pe-auto store-banners-carousel__tab${
                  isActive ? " store-banners-carousel__tab--active" : ""
                }`}
                onClick={() => goToIndex(index)}
              >
                <span className="d-block text-truncate w-100">
                  {banner.tabText}
                </span>
              </button>
            );
          })}
        </div>
        {showTabChevrons ? (
          <button
            type="button"
            className="btn btn-link btn-sm d-flex align-items-center flex-shrink-0 text-black pe-auto px-1"
            onClick={scrollTabsRight}
            disabled={!canScrollTabsRight}
            aria-label="Next banner tabs"
          >
            <i className="fa-solid fa-chevron-right" aria-hidden />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function StoreHighlightFolderPills({ items, onNavigate }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div
      className="d-flex flex-nowrap overflow-auto gap-1 px-3 pb-2"
      aria-label="Top folders"
    >
      {items.map((item, index) => (
        <button
          key={`${item.name}-${index}`}
          type="button"
          className="btn btn-sm rounded-pill border text-secondary flex-shrink-0 py-0 px-2 small fw-medium"
          onClick={() => onNavigate(item.href)}
        >
          {item.name}
        </button>
      ))}
    </div>
  );
}

function StoreTopFoldersCarousel({ items, onNavigate }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div
      className="d-flex overflow-auto gap-2 px-3 pb-2"
      aria-label="Top folders"
    >
      {items.map((item, index) => (
        <div
          key={`${item.name}-${index}`}
          className="flex-shrink-0 text-center"
          style={{ width: "60px" }}
        >
          <button
            type="button"
            className="btn p-0 border-0 bg-transparent w-100 text-center"
            onClick={() => onNavigate(item.href)}
          >
            <div
              className="rounded-2 p-1 overflow-hidden border"
              style={{
                background: "linear-gradient(180deg, #FAF8F3 0%, #E6E0D5 100%)",
                border: "1px solid #e3dccf",
              }}
            >
              <img
                src={item.imageUrl}
                alt={item.name}
                className="w-100 object-fit-contain d-block"
                style={{ height: "60px" }}
                loading="lazy"
              />
            </div>
            <div className="small text-secondary mt-1 text-truncate">
              {item.name}
            </div>
          </button>
        </div>
      ))}
    </div>
  );
}

function StoreTopFoldersCarouselSkeleton({ items = 7 }) {
  return (
    <div
      className="d-flex overflow-auto gap-2 px-3 pb-2 mt-3"
      aria-label="Top folders loading"
      aria-hidden="true"
    >
      {Array.from({ length: items }, (_, idx) => (
        <div
          key={`top-folder-skeleton-${idx}`}
          className="flex-shrink-0 text-center"
          style={{ width: "60px" }}
        >
          <div className="store-products-skeleton__block store-top-folders-skeleton__thumb rounded-2 border" />
          <div className="store-products-skeleton__block store-top-folders-skeleton__label mt-1" />
        </div>
      ))}
    </div>
  );
}

function StoreHighlightsGrid() {
  return (
    <div className="mt-3 px-3">
      <div
        className="row row-cols-2 row-cols-lg-4 g-0 overflow-hidden rounded-3 border"
        aria-label="Store highlights"
      >
        {STORE_HIGHLIGHTS.map((item) => (
          <div key={item.title} className="col border-end border-bottom">
            <div className="h-100 bg-white py-2 px-2 d-flex align-items-stretch">
              <div
                className="d-flex align-self-stretch align-items-center justify-content-center text-secondary pe-2"
                style={{ minWidth: 32, fontSize: "1.25rem" }}
                aria-hidden
              >
                <i className={item.icon} />
              </div>
              <div className="d-flex flex-column justify-content-center text-start">
                <div
                  className="fw-bold text-primary text-uppercase lh-sm mb-1"
                  style={{ fontSize: "0.6rem" }}
                >
                  {item.title}
                </div>
                {item.subText ? (
                  <div
                    className="text-secondary lh-sm"
                    style={{ fontSize: "0.67rem" }}
                  >
                    {item.subText}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function initialSearchQueryFromLocation(search) {
  const params = new URLSearchParams(search || "");
  const q = params.get("search");
  return q == null ? "" : String(q);
}

const StoreProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { code: activeCodeParam } = useParams();
  const {
    currentUser,
    openAuthModal,
    isAuthModalOpen = false,
    storeProjectsList: projectsList = [],
    setStoreProjectsList: setProjectsList,
    refreshStoreProjects,
  } = useContext(AppContext) || {};
  const isTempGuestUser =
    Boolean(currentUser?.temp === true) ||
    String(currentUser?.mobile ?? "")
      .toLowerCase()
      .startsWith("guest_");
  const canShowPrice = Boolean(currentUser) && !isTempGuestUser;
  const isLoggedIn = Boolean(currentUser?.mobile);
  /** Logged-in users can pick a project; guests use cart add flow. */
  const isNonCartUser = isLoggedIn && !isTempGuestUser;
  const isCustomerUser = isLoggedIn && currentUser?.customer === true;
  const isActiveCustomerUser = isActiveStoreCustomer(currentUser);
  const isDealerByType =
    typeof currentUser?.type === "string" &&
    currentUser.type.toLowerCase().includes("dealer");
  const isDealerUser =
    isLoggedIn && (currentUser?.dealer === true || isDealerByType);
  const isAdminUser = isLoggedIn && currentUser?.admin === true;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folders, setFolders] = useState([]);
  const [highlightFolders, setHighlightFolders] = useState([]);
  const [, setTotals] = useState({
    total_catalog_products: null,
    total_entries: null,
  });
  const [hasProjects, setHasProjects] = useState(null);
  const [openFolderIds, setOpenFolderIds] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState(() =>
    initialSearchQueryFromLocation(location.search)
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  /** Request in flight; combined with debounce for subtle full-width loading veil. */
  const [searchOverlay, setSearchOverlay] = useState(false);
  const [searchResultFolders, setSearchResultFolders] = useState(null);
  const [searchFetchError, setSearchFetchError] = useState(null);
  const [busyByProductId, setBusyByProductId] = useState({});
  const [addedCountByProductId, setAddedCountByProductId] = useState({});
  const [addedFlashByProductId, setAddedFlashByProductId] = useState({});
  const [notice, setNotice] = useState(null);
  /** Success banner after add — same pattern as ProductDetailModal (`Added to your project.` + optional link). */
  const [addSuccess, setAddSuccess] = useState(null);
  const [copiedNotice, setCopiedNotice] = useState(false);
  const catalogBodyRef = useRef(null);
  const catalogProductOrdinalRef = useRef(0);
  const lastUsedProjectIdRef = useRef(null);
  const addedFlashTimeoutsRef = useRef({});
  const searchShellStickySentinelRef = useRef(null);
  const [isSearchShellStuck, setIsSearchShellStuck] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const searchInputRef = useRef(null);
  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
  const [isViewQuotePickerOpen, setIsViewQuotePickerOpen] = useState(false);
  const [createQuoteModalState, setCreateQuoteModalState] = useState({
    isOpen: false,
    product: null,
    folderPath: [],
  });
  const [folderPickerModal, setFolderPickerModal] = useState({
    isOpen: false,
    projectId: null,
    projectName: "",
    product: null,
  });
  const [createQuoteName, setCreateQuoteName] = useState("");
  const [createQuoteBusy, setCreateQuoteBusy] = useState(false);
  const skipNextFocusScrollRef = useRef(false);
  /** URL-backed product detail state (`/products/:code`) with modal metadata. */
  const [activeProductModalState, setActiveProductModalState] = useState(null);
  const lastHandledFolderQueryRef = useRef(null);
  const shouldAutoFocusSearch = Boolean(location.state?.focusSearch);
  const defaultBannerId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");

    return params.get("b");
  }, [location.search]);
  const disableBannerAutoplay = useMemo(() => {
    const params = new URLSearchParams(location.search || "");

    return params.has("b");
  }, [location.search]);
  const topLevelCarouselItems = useMemo(() => {
    if (!Array.isArray(highlightFolders)) return [];
    return highlightFolders
      .map((folder) => {
        const href = String(folder?.url ?? "").trim();
        const imageUrlRaw = folder?.image_url;
        return {
          href,
          name: String(folder?.name ?? "").trim() || "Folder",
          imageUrl:
            typeof imageUrlRaw === "string" && imageUrlRaw.trim()
              ? imageUrlRaw.trim()
              : null,
        };
      })
      .filter((item) => item.imageUrl && item.href);
  }, [highlightFolders]);
  const highlightFolderPillItems = useMemo(() => {
    if (!Array.isArray(highlightFolders)) return [];
    return highlightFolders
      .map((folder) => {
        const href = String(folder?.url ?? "").trim();
        return {
          href,
          name: String(folder?.name ?? "").trim() || "Folder",
        };
      })
      .filter((item) => item.href);
  }, [highlightFolders]);

  const draftQuotes = useMemo(
    () =>
      projectsList.filter((proj) => {
        const status = String(proj?.status ?? "")
          .trim()
          .toLowerCase();
        return status === "" || status === "draft";
      }),
    [projectsList]
  );
  const ownDraftQuotes = useMemo(
    () => filterOwnStoreDraftQuotesForAdd(projectsList, currentUser),
    [projectsList, currentUser]
  );
  const currentUserId = useMemo(
    () => normalizeStoreUserId(currentUser),
    [currentUser]
  );
  const totalDraftQuoteItems = useMemo(
    () =>
      draftQuotes.reduce((sum, proj) => {
        if (!isOwnStoreProject(proj, currentUserId)) return sum;
        const itemsCount = Number(proj?.items_count ?? 0);
        return sum + (Number.isFinite(itemsCount) ? itemsCount : 0);
      }, 0),
    [draftQuotes, currentUserId]
  );
  const hasDraftQuoteItemsCount = useMemo(
    () =>
      draftQuotes.some((proj) =>
        Number.isFinite(Number(proj?.items_count ?? Number.NaN))
      ),
    [draftQuotes]
  );
  const dockAddedItems = totalDraftQuoteItems;
  const shouldShowDock = draftQuotes.length > 0 && dockAddedItems > 0;
  const viewQuoteProjectId = useMemo(() => {
    const addId = toNullableNumber(addSuccess?.projectId);
    if (addId != null) return addId;
    const sorted = sortStoreProjectsEarliestFirst(ownDraftQuotes);
    const first = sorted[0];
    return toNullableNumber(first?.id ?? first?.project_id);
  }, [addSuccess?.projectId, ownDraftQuotes]);
  const viewQuoteHref =
    viewQuoteProjectId != null ? `/projects/${viewQuoteProjectId}` : null;

  const isSingleSampleOrderDock = useMemo(() => {
    if (projectsList.length !== 1) return false;
    return isStoreSampleQuote(projectsList[0]);
  }, [projectsList]);

  const sampleCabinetProject = useMemo(
    () => projectsList.find((proj) => isStoreSampleQuote(proj)) ?? null,
    [projectsList]
  );
  const hasSampleCabinetQuote = sampleCabinetProject != null;
  const showSampleCabinetCta = !isDealerUser && !isAdminUser && !isCustomerUser;

  const handleSampleCabinetQuoteCreated = useCallback((project) => {
    if (!project || typeof project !== "object") return;
    const newId = toNullableNumber(project?.id ?? project?.project_id);
    setProjectsList((prev) =>
      sortStoreProjectsEarliestFirst([
        project,
        ...prev.filter(
          (p) => toNullableNumber(p?.id ?? p?.project_id) !== newId
        ),
      ])
    );
    setHasProjects(true);
    if (newId != null) {
      setAddSuccess({ projectId: newId });
    }
  }, []);

  const handleSampleCabinetAuthRequired = useCallback(() => {
    try {
      sessionStorage.setItem(PENDING_SAMPLE_CABINET_QUOTE_KEY, "1");
    } catch {
      // ignore
    }
    if (typeof openAuthModal === "function") {
      openAuthModal({
        returnTo: {
          pathname: location.pathname,
          search: location.search,
        },
      });
    }
  }, [openAuthModal, location.pathname, location.search]);

  const handlePromoBannerAuthRequired = useCallback(() => {
    if (typeof openAuthModal === "function") {
      openAuthModal({
        returnTo: {
          pathname: location.pathname,
          search: location.search,
        },
      });
    }
  }, [openAuthModal, location.pathname, location.search]);

  const [deliveryChargesModalOpen, setDeliveryChargesModalOpen] =
    useState(false);

  const handleSampleCabinetBannerClick = useCallback(async () => {
    if (!isLoggedIn) {
      handleSampleCabinetAuthRequired();
      return;
    }
    if (sampleCabinetProject) {
      const projectId = toNullableNumber(
        sampleCabinetProject?.id ?? sampleCabinetProject?.project_id
      );
      if (projectId != null) {
        navigate(`/projects/${projectId}`, {
          state: { project: sampleCabinetProject },
        });
      }

      return;
    }

    try {
      const res = await createSampleCabinetQuote();
      const body = res?.data ?? res;
      const project =
        body?.data?.project ?? body?.data ?? body?.project ?? body;
      handleSampleCabinetQuoteCreated(project);
      const projectId = toNullableNumber(project?.id ?? project?.project_id);
      if (projectId != null) {
        navigate(`/projects/${projectId}`, { state: { project } });
      }
    } catch (e) {
      const message =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        "Unable to start sample cabinet quote.";
      setError(message);
    }
  }, [
    isLoggedIn,
    handleSampleCabinetAuthRequired,
    sampleCabinetProject,
    handleSampleCabinetQuoteCreated,
    navigate,
  ]);

  const handleFirstOrderOfferBannerClick = useCallback(() => {
    if (!isLoggedIn) {
      setPendingQuoteDrawingWhatsAppIntent();
      handlePromoBannerAuthRequired();
      return;
    }

    executeQuoteDrawingWhatsAppIntent();
  }, [isLoggedIn, handlePromoBannerAuthRequired]);

  const listingBanners = useMemo(() => {
    return BANNER_DEFINITIONS.map((def) => {
      let actionText = def.defaultActionText;
      let actionOnClick;
      let showAction = true;

      if (def.id === "sample_cabinet") {
        if (!showSampleCabinetCta) {
          showAction = false;
        } else {
          actionText = hasSampleCabinetQuote
            ? "View Sample Order"
            : "Order Sample Cabinet";
          actionOnClick = () => {
            void handleSampleCabinetBannerClick();
          };
        }
      } else if (def.id === "free_shipping") {
        actionOnClick = () => setDeliveryChargesModalOpen(true);
      } else if (def.id === "first_order") {
        actionOnClick = handleFirstOrderOfferBannerClick;
      }

      return {
        id: def.id,
        image: def.image,
        tabText: def.tabText,
        actionText: showAction ? actionText : null,
        actionOnClick: showAction ? actionOnClick : null,
      };
    });
  }, [
    showSampleCabinetCta,
    hasSampleCabinetQuote,
    handleSampleCabinetBannerClick,
    handleFirstOrderOfferBannerClick,
  ]);

  const catalogPromoBannerProps = useMemo(
    () => ({
      isLoggedIn,
      onAuthRequired: handlePromoBannerAuthRequired,
      sampleCabinetVisible: showSampleCabinetCta,
      sampleCabinetProject: sampleCabinetProject,
      sampleCabinetIsLoggedIn: isLoggedIn,
      onSampleCabinetAuthRequired: handleSampleCabinetAuthRequired,
      onSampleCabinetQuoteCreated: handleSampleCabinetQuoteCreated,
      onSampleCabinetError: (message) => setError(message),
      hidden: isActiveCustomerUser,
    }),
    [
      isLoggedIn,
      handlePromoBannerAuthRequired,
      showSampleCabinetCta,
      sampleCabinetProject,
      handleSampleCabinetAuthRequired,
      handleSampleCabinetQuoteCreated,
      isActiveCustomerUser,
    ]
  );

  const pendingSampleCreateRef = useRef(false);
  useEffect(() => {
    if (!isLoggedIn || isAuthModalOpen) return;
    if (!hasPendingSampleCabinetIntent()) return;
    if (pendingSampleCreateRef.current) return;
    if (sampleCabinetProject) {
      clearPendingSampleCabinetIntent();
      return;
    }

    pendingSampleCreateRef.current = true;
    clearPendingSampleCabinetIntent();

    (async () => {
      try {
        const res = await createSampleCabinetQuote();
        const body = res?.data ?? res;
        const project =
          body?.data?.project ?? body?.data ?? body?.project ?? body;
        const projectId = Number(project?.id ?? project?.project_id);
        handleSampleCabinetQuoteCreated(project);
        if (Number.isFinite(projectId) && projectId > 0) {
          navigate(`/projects/${projectId}`, { state: { project } });
        }
      } catch (e) {
        const message =
          e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          e?.message ||
          "Unable to start sample cabinet quote.";
        setError(message);
      } finally {
        pendingSampleCreateRef.current = false;
      }
    })();
  }, [
    isLoggedIn,
    isAuthModalOpen,
    sampleCabinetProject,
    handleSampleCabinetQuoteCreated,
    navigate,
  ]);

  useEffect(() => {
    if (!isLoggedIn || isAuthModalOpen) return;
    if (hasPendingPanelSamplePhoneIntent()) {
      clearPendingPanelSamplePhoneIntent();
      executePanelSamplePhoneIntent();
      return;
    }
    if (hasPendingQuoteDrawingWhatsAppIntent()) {
      clearPendingQuoteDrawingWhatsAppIntent();
      executeQuoteDrawingWhatsAppIntent();
    }
  }, [isLoggedIn, isAuthModalOpen]);

  const openProductDetailFromRow = useCallback(
    (product) => {
      const productId = resolveStoreCatalogListProductId(product);
      const code = resolveStoreCatalogProductCode(product);
      if (productId == null || !code) return;
      const nextState = {
        from: location.pathname + location.search,
        initialAdded: Number(product?.added ?? 0),
        initialHasProjects: hasProjects,
        initialFolderPath: Array.isArray(product?.folder_path)
          ? product.folder_path
          : [],
      };
      setActiveProductModalState(nextState);
      navigate(`/products/${encodeURIComponent(codeToUrlCode(code))}`, {
        state: nextState,
      });
    },
    [hasProjects, location.pathname, location.search, navigate]
  );

  useEffect(() => {
    if (activeCodeParam) {
      setActiveProductModalState((prev) => {
        if (prev) return prev;
        const state = location.state;
        return {
          from:
            typeof state?.from === "string" && state.from.startsWith("/")
              ? state.from
              : "/products",
          initialAdded: Number(state?.initialAdded ?? 0),
          initialHasProjects: state?.initialHasProjects,
          initialFolderPath: Array.isArray(state?.initialFolderPath)
            ? state.initialFolderPath
            : [],
        };
      });
      return;
    }
    setActiveProductModalState(null);
  }, [activeCodeParam, location.state]);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearchQuery(searchQuery.trim()),
      300
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const next = initialSearchQueryFromLocation(location.search);
    setSearchQuery((prev) => (prev === next ? prev : next));
  }, [location.search]);

  const mergeCatalogProjectsIntoList = useCallback(
    (body) => {
      if (!body || typeof setProjectsList !== "function") return;
      const catalogProjects = parseStoreProjectsFromPayload(body, currentUser);
      if (catalogProjects.length === 0) return;
      setProjectsList((prev) => {
        const merged = mergeBuyerAccountProjectLists(catalogProjects, prev);
        return sortStoreProjectsForBuyerAccount(merged, currentUser);
      });
      setHasProjects(true);
    },
    [currentUser, setProjectsList]
  );

  const mergeCatalogProjectsRef = useRef(mergeCatalogProjectsIntoList);
  mergeCatalogProjectsRef.current = mergeCatalogProjectsIntoList;

  useEffect(() => {
    if (!catalogBodyRef.current) return;
    mergeCatalogProjectsRef.current(catalogBodyRef.current);
  }, [currentUser?.id]);

  useEffect(() => {
    if (!shouldAutoFocusSearch) return;
    setIsSearchInputFocused(true);
    skipNextFocusScrollRef.current = true;
    const t = setTimeout(() => {
      const input = searchInputRef.current;
      if (input) input.focus();
      const shell = searchShellStickySentinelRef.current;
      if (shell) {
        const y = window.pageYOffset + shell.getBoundingClientRect().top;
        window.scrollTo({ top: Math.max(y, 0), behavior: "auto" });
      }
    }, 0);
    return () => clearTimeout(t);
  }, [shouldAutoFocusSearch]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const el = searchShellStickySentinelRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        const stuck = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        setIsSearchShellStuck(stuck);
      },
      { threshold: [0] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    trackZbListingView();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const body = await loadStoreProductsResponseBody();
        if (!isMounted) return;
        catalogBodyRef.current = body;
        const parsed = parseStoreProductsPayload(body);
        setFolders(parsed.folders);
        setHighlightFolders(parsed.highlight_folders);
        setOpenFolderIds(new Set(collectAllFolderNodeKeys(parsed.folders)));
        setAddedCountByProductId(collectAddedCountsFromFolders(parsed.folders));
        setTotals({
          total_catalog_products: parsed.total_catalog_products,
          total_entries: parsed.total_entries,
        });
        setHasProjects(parsed.has_projects);
        mergeCatalogProjectsRef.current(body);
      } catch (e) {
        if (!isMounted) return;
        setError(getApiErrorMessage(e, "Unable to load products."));
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const q = debouncedSearchQuery;
    if (!q) {
      setSearchOverlay(false);
      setSearchResultFolders(null);
      setSearchFetchError(null);
      return;
    }
    let isMounted = true;
    setSearchOverlay(true);
    setSearchFetchError(null);
    const loadSearch = async () => {
      try {
        const body = await loadStoreProductsSearchResponseBody(q);
        if (!isMounted) return;
        const parsed = parseStoreProductsPayload(body);
        setSearchResultFolders(parsed.folders);
        setHighlightFolders(parsed.highlight_folders);
      } catch (e) {
        if (!isMounted) return;
        setSearchFetchError(getApiErrorMessage(e, "Search failed."));
        setSearchResultFolders(null);
      } finally {
        if (isMounted) setSearchOverlay(false);
      }
    };
    loadSearch();
    return () => {
      isMounted = false;
    };
  }, [debouncedSearchQuery]);

  /** Open accordion nodes for the search tree so results are visible; user can still collapse. */
  useEffect(() => {
    if (
      !Array.isArray(searchResultFolders) ||
      searchResultFolders.length === 0
    ) {
      return;
    }
    const keys = collectAllFolderNodeKeys(searchResultFolders);
    if (keys.length === 0) return;
    setOpenFolderIds((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.add(k);
      return next;
    });
  }, [searchResultFolders]);

  const trimmedSearch = searchQuery.trim();
  const isSearchActive = trimmedSearch.length > 0;
  const shouldHideTopIntro = isSearchInputFocused || isSearchActive;
  const showMobileSearchChrome =
    !isMobile || isSearchShellStuck || shouldHideTopIntro;
  const showMobileHighlightPills =
    isMobile &&
    isSearchShellStuck &&
    !shouldHideTopIntro &&
    highlightFolderPillItems.length > 0;
  const showDesktopTopFolders =
    !isMobile &&
    !shouldHideTopIntro &&
    !isLoading &&
    !error &&
    topLevelCarouselItems.length > 0;
  const showDesktopTopFoldersSkeleton =
    !isMobile && !shouldHideTopIntro && isLoading;
  const showStickySampleCabinetCta =
    isMobile &&
    isSearchShellStuck &&
    !shouldHideTopIntro &&
    showSampleCabinetCta;
  const isProductsRoute = /^\/(?:store\/)?products(?:\/|$)/.test(
    location.pathname
  );
  const searchPendingDebounce =
    trimmedSearch.length > 0 && trimmedSearch !== debouncedSearchQuery;
  const searchLoadingOverlay = searchPendingDebounce || searchOverlay;
  /** Search request finished (success or empty); hide full catalog so only search UI shows. */
  const searchResolved =
    isSearchActive &&
    !searchLoadingOverlay &&
    !searchFetchError &&
    searchResultFolders !== null;

  const toggleFolder = (nodeKey) => {
    const id = String(nodeKey);
    setOpenFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isFolderExpanded = (nodeKey) => {
    const id = String(nodeKey);
    return openFolderIds.has(id);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setSearchOverlay(false);
    setSearchResultFolders(null);
    setSearchFetchError(null);
  };

  const handleCarouselFolderClick = (nodeKey, ancestorNodeKeys = []) => {
    const id = String(nodeKey ?? "");
    if (!id) return;
    if (searchResolved) clearSearch();
    setOpenFolderIds((prev) => {
      const next = new Set(prev);
      for (const ancestorNodeKey of ancestorNodeKeys) {
        next.add(String(ancestorNodeKey));
      }
      next.add(id);
      return next;
    });
    setTimeout(() => {
      const el = document.getElementById(folderDomId(id));
      if (el) {
        const navOffset = 72;
        const extraGap = 8;
        const y =
          window.pageYOffset +
          el.getBoundingClientRect().top -
          navOffset -
          extraGap;
        window.scrollTo({ top: Math.max(y, 0), behavior: "smooth" });
      }
    }, 0);
  };

  useEffect(() => {
    if (isLoading || error || !Array.isArray(folders) || folders.length === 0) {
      return;
    }
    const params = new URLSearchParams(location.search || "");
    const folderQuery = params.get("folder");
    const normalized = normalizeFolderQueryToken(folderQuery);
    if (!normalized) {
      lastHandledFolderQueryRef.current = null;
      return;
    }
    if (lastHandledFolderQueryRef.current === normalized) return;

    const folderMatch = findFolderNodeMatchByQuery(folders, normalized);
    if (!folderMatch?.nodeKey) return;

    lastHandledFolderQueryRef.current = normalized;
    handleCarouselFolderClick(
      folderMatch.nodeKey,
      folderMatch.ancestorNodeKeys || []
    );
  }, [folders, isLoading, error, location.search]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 1800);
    return () => clearTimeout(t);
  }, [notice]);

  useEffect(() => {
    if (!isProductsRoute) return undefined;
    const navEl = document.querySelector(".store-layout nav.navbar");
    const mainEl = document.querySelector(".store-layout .store-main");
    if (!navEl && !mainEl) return undefined;

    const prevNavDisplay = navEl ? navEl.style.display : null;
    const prevMainPaddingTop = mainEl ? mainEl.style.paddingTop : null;

    if (shouldHideTopIntro) {
      if (navEl) navEl.style.display = "none";
      if (mainEl) mainEl.style.paddingTop = "0px";
    } else {
      if (navEl) navEl.style.display = prevNavDisplay ?? "";
      if (mainEl) mainEl.style.paddingTop = prevMainPaddingTop ?? "";
    }

    return () => {
      // Avoid leaking stale inline styles when navigating away (e.g. to /projects/:id).
      if (!/^\/(?:store\/)?products(?:\/|$)/.test(window.location.pathname)) {
        return;
      }
      if (navEl) navEl.style.display = prevNavDisplay ?? "";
      if (mainEl) mainEl.style.paddingTop = prevMainPaddingTop ?? "";
    };
  }, [isProductsRoute, shouldHideTopIntro]);

  useEffect(() => {
    if (!copiedNotice) return undefined;
    const t = setTimeout(() => setCopiedNotice(false), 2000);
    return () => clearTimeout(t);
  }, [copiedNotice]);

  const handleCopyCode = useCallback(async (code) => {
    const value = String(code ?? "").trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        ta.remove();
      }
      setCopiedNotice(true);
    } catch {
      setNotice("Unable to copy code.");
    }
  }, []);

  const getProjectNameById = useCallback(
    (projectId) => {
      const pid = toNullableNumber(projectId);
      if (pid == null) return null;
      const match = projectsList.find(
        (proj) => toNullableNumber(proj?.id ?? proj?.project_id) === pid
      );
      const name = String(match?.name ?? "").trim();
      return name || null;
    },
    [projectsList]
  );
  const incrementProjectItemsCount = useCallback((projectId, delta = 1) => {
    const pid = toNullableNumber(projectId);
    const step = Number(delta);
    if (pid == null || !Number.isFinite(step) || step === 0) return;
    setProjectsList((prev) => {
      let matched = false;
      const next = prev.map((proj) => {
        const projId = toNullableNumber(proj?.id ?? proj?.project_id);
        if (projId !== pid) return proj;
        matched = true;
        const current = Number(proj?.items_count ?? 0);
        const safeCurrent = Number.isFinite(current) ? current : 0;
        return {
          ...proj,
          items_count: Math.max(0, safeCurrent + step),
        };
      });
      if (matched) return next;
      return [
        ...next,
        { id: pid, name: `Quote ${pid}`, status: "draft", items_count: step },
      ];
    });
  }, []);

  const showAddedToCartFlash = useCallback((productId, _projectName) => {
    const pid = toNullableNumber(productId);
    if (pid == null) return;
    setAddedFlashByProductId((prev) => ({ ...prev, [pid]: "Added" }));
    const existing = addedFlashTimeoutsRef.current[pid];
    if (existing) clearTimeout(existing);
    addedFlashTimeoutsRef.current[pid] = setTimeout(() => {
      setAddedFlashByProductId((prev) => {
        const next = { ...prev };
        delete next[pid];
        return next;
      });
      delete addedFlashTimeoutsRef.current[pid];
    }, 2000);
  }, []);

  useEffect(
    () => () => {
      Object.values(addedFlashTimeoutsRef.current).forEach((t) =>
        clearTimeout(t)
      );
      addedFlashTimeoutsRef.current = {};
    },
    []
  );

  const ensureFirstProjectId = useCallback(async () => {
    let id = firstManageableProjectIdFromList(projectsList);
    if (id != null) return id;
    if (typeof refreshStoreProjects !== "function") return null;
    try {
      const fetched = await refreshStoreProjects();
      if (fetched.length > 0) {
        id = firstManageableProjectIdFromList(fetched);
        if (id != null) return id;
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  }, [projectsList, refreshStoreProjects]);

  const completeProjectAddAfterApi = useCallback(
    (projectId, product, projectName, quantity = 1) => {
      const productId = resolveStoreCatalogListProductId(product);
      if (!productId || !projectId) return;
      const qty = Number(quantity);
      const addCount = Number.isFinite(qty) && qty > 0 ? qty : 1;
      lastUsedProjectIdRef.current = projectId;
      trackZbAddToCart(
        product?.name ?? "Product",
        productId,
        product?.discount_price ?? product?.price ?? 0
      );
      setHasProjects(true);
      incrementProjectItemsCount(projectId, addCount);
      setAddedCountByProductId((prev) => ({
        ...prev,
        [productId]:
          Number(prev?.[productId] ?? product?.added ?? 0) + addCount,
      }));
      setError(null);
      setAddSuccess({ projectId: toNullableNumber(projectId) });
      showAddedToCartFlash(
        productId,
        projectName || getProjectNameById(projectId)
      );
    },
    [getProjectNameById, incrementProjectItemsCount, showAddedToCartFlash]
  );

  const openQuoteFolderPicker = useCallback(
    (projectId, projectName, product) => {
      const pid = toNullableNumber(projectId);
      if (pid == null || !product) return;
      setFolderPickerModal({
        isOpen: true,
        projectId: pid,
        projectName:
          String(projectName ?? "").trim() || getProjectNameById(pid) || "",
        product,
      });
    },
    [getProjectNameById]
  );

  const handleFolderPickerAdded = useCallback(
    ({ projectId, productId, quantity }) => {
      const product = folderPickerModal.product;
      const resolvedId = resolveStoreCatalogListProductId(product);
      if (
        !product ||
        toNullableNumber(projectId) == null ||
        resolvedId !== toNullableNumber(productId)
      ) {
        return;
      }
      completeProjectAddAfterApi(
        projectId,
        product,
        folderPickerModal.projectName,
        quantity
      );
      setFolderPickerModal({
        isOpen: false,
        projectId: null,
        projectName: "",
        product: null,
      });
    },
    [
      completeProjectAddAfterApi,
      folderPickerModal.product,
      folderPickerModal.projectName,
    ]
  );

  const handleAddProject = (projectId, projectName, product, _folderPath) => {
    const productId = resolveStoreCatalogListProductId(product);
    if (!productId) return;
    if (toNullableNumber(projectId) == null) {
      handleCreateProjectAndAdd(product, _folderPath);
      return;
    }
    openQuoteFolderPicker(projectId, projectName, product);
  };

  const createProjectAndAddByName = useCallback(
    async (product, _folderPath, rawProjectName) => {
      const productId = resolveStoreCatalogListProductId(product);
      if (!productId) return;
      const projectName = String(rawProjectName ?? "").trim();
      if (!projectName) {
        setError("Enter project name.");
        return;
      }
      setBusyByProductId((prev) => ({ ...prev, [productId]: true }));
      try {
        const res = await createStoreProject({ name: projectName });
        const body = res?.data ?? res;
        const project =
          body?.data?.project ?? body?.project ?? body?.data ?? body;
        const newId = toNullableNumber(
          project?.id ??
            project?.project_id ??
            body?.id ??
            body?.data?.id ??
            body?.data?.project?.id
        );
        if (!newId) {
          setError("Project created but the response did not include an id.");
          return;
        }
        const listEntry =
          project && (project.id || project.project_id)
            ? project
            : { id: newId, name: projectName };
        setProjectsList((prev) =>
          sortStoreProjectsEarliestFirst([...prev, listEntry])
        );
        openQuoteFolderPicker(
          newId,
          String(listEntry?.name ?? "").trim() || projectName,
          product
        );
      } catch (e) {
        setError(
          e?.response?.data?.message ||
            e?.response?.data?.error ||
            e?.message ||
            "Failed to create project."
        );
      } finally {
        setBusyByProductId((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [openQuoteFolderPicker]
  );

  const closeFolderPickerModal = useCallback(() => {
    setFolderPickerModal({
      isOpen: false,
      projectId: null,
      projectName: "",
      product: null,
    });
  }, []);

  const handleCreateProjectAndAdd = useCallback(
    (product, folderPath) => {
      if (createQuoteBusy || !product) return;
      closeFolderPickerModal();

      const needsProjectName = shouldPromptStoreProjectName({
        projects: projectsList,
        hasProjects,
      });

      if (!needsProjectName) {
        setCreateQuoteBusy(true);
        void (async () => {
          try {
            await createProjectAndAddByName(
              product,
              Array.isArray(folderPath) ? folderPath : [],
              MY_FIRST_QUOTE_PROJECT_NAME
            );
          } finally {
            setCreateQuoteBusy(false);
          }
        })();
        return;
      }

      setCreateQuoteName("");
      setCreateQuoteModalState({
        isOpen: true,
        product,
        folderPath: Array.isArray(folderPath) ? folderPath : [],
      });
    },
    [
      closeFolderPickerModal,
      createProjectAndAddByName,
      createQuoteBusy,
      hasProjects,
      projectsList,
    ]
  );

  const submitCreateQuoteModal = useCallback(async () => {
    if (createQuoteBusy) return;
    const quoteName = String(createQuoteName ?? "").trim();
    if (!quoteName) {
      setError("Enter project name.");
      return;
    }
    const product = createQuoteModalState.product;
    if (!product) return;
    setCreateQuoteBusy(true);
    try {
      await createProjectAndAddByName(
        product,
        createQuoteModalState.folderPath,
        quoteName
      );
      setCreateQuoteModalState({
        isOpen: false,
        product: null,
        folderPath: [],
      });
      setCreateQuoteName("");
    } finally {
      setCreateQuoteBusy(false);
    }
  }, [
    createProjectAndAddByName,
    createQuoteBusy,
    createQuoteModalState.folderPath,
    createQuoteModalState.product,
    createQuoteName,
  ]);

  const handleAddCart = async (product, folderPath) => {
    const productId = resolveStoreCatalogListProductId(product);
    if (!productId) return;
    setBusyByProductId((prev) => ({ ...prev, [productId]: true }));
    try {
      const path = normalizeStoreProjectFolderPath(
        Array.isArray(product?.folder_path) ? product.folder_path : folderPath
      );
      if (hasProjects !== true) {
        const cartResp = await addStoreProductToCartProject(productId, {
          folderPath: path,
          projectName: MY_FIRST_QUOTE_PROJECT_NAME,
        });
        const proj = cartResp?.data?.project ?? cartResp?.project ?? null;
        const newPid = toNullableNumber(proj?.id ?? proj?.project_id);
        if (proj != null && newPid != null) {
          setProjectsList((prev) =>
            sortStoreProjectsEarliestFirst([
              proj,
              ...prev.filter(
                (p) => toNullableNumber(p?.id ?? p?.project_id) !== newPid
              ),
            ])
          );
        }
        trackZbAddToCart(
          product?.name ?? "Product",
          productId,
          product?.discount_price ?? product?.price ?? 0
        );
        setHasProjects(true);
        showAddedToCartFlash(productId, MY_FIRST_QUOTE_PROJECT_NAME);
      } else {
        const projectId = await ensureFirstProjectId();
        if (!projectId) throw new Error("No project found.");
        await createStoreProjectItem(projectId, {
          item_type: "product",
          product_id: productId,
          ...(path.length > 0 ? { folder_path: path } : {}),
        });
        trackZbAddToCart(
          product?.name ?? "Product",
          productId,
          product?.discount_price ?? product?.price ?? 0
        );
        setError(null);
        setAddSuccess({ projectId });
        incrementProjectItemsCount(projectId, 1);
        showAddedToCartFlash(productId, getProjectNameById(projectId));
      }
      setAddedCountByProductId((prev) => ({
        ...prev,
        [productId]: Number(prev?.[productId] ?? product?.added ?? 0) + 1,
      }));
      if (hasProjects !== true) {
        setError(null);
        const linkPid = await ensureFirstProjectId();
        setAddSuccess({ projectId: linkPid });
      }
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Unable to add to quote."
      );
    } finally {
      setBusyByProductId((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleProjectIncrement = async (product, folderPath) => {
    const productId = resolveStoreCatalogListProductId(product);
    if (!productId) return;
    let projectId = lastUsedProjectIdRef.current;
    const sortedForIncrement = sortStoreProjectsEarliestFirst(
      filterOwnStoreDraftQuotesForAdd(projectsList, currentUser)
    );
    if (!projectId && sortedForIncrement.length > 0) {
      projectId = resolveStoreProjectEntityId(sortedForIncrement[0]);
    }
    if (!projectId) {
      setError("Select a project first.");
      return;
    }
    await handleAddProject(projectId, null, product, folderPath);
  };

  const handleRemoveProductFromProject = async (product, folderPath) => {
    const productId = resolveStoreCatalogListProductId(product);
    const current = Number(
      addedCountByProductId?.[productId] ?? product?.added ?? 0
    );
    if (!productId || current <= 0) return;
    setBusyByProductId((prev) => ({ ...prev, [productId]: true }));
    try {
      const sortedForRemove = sortStoreProjectsEarliestFirst(
        filterOwnStoreDraftQuotesForAdd(projectsList, currentUser)
      );
      const firstProject = sortedForRemove[0];
      const projectId = resolveStoreProjectEntityId(firstProject);
      if (!projectId) throw new Error("No project found.");

      const itemsBody = await getStoreProjectItems(projectId);
      const items = flattenProjectItemsTree(
        parseProjectItemsResponseBody(itemsBody)
      );

      const rawPath = Array.isArray(product?.folder_path)
        ? product.folder_path
        : folderPath;
      const { parentId, pathResolved } = walkExistingFolderPathInItems(
        items,
        rawPath
      );

      let candidates = [];
      if (pathResolved) {
        candidates = items
          .filter((it) => {
            if (it?.item_type !== "product") return false;
            if ((it?.parent_id ?? null) !== parentId) return false;
            const pid = resolveProjectLineStoreProductId(it);
            return Number(pid) === Number(productId);
          })
          .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0));
      }
      if (candidates.length === 0) {
        candidates = items
          .filter((it) => {
            if (it?.item_type !== "product") return false;
            const pid = resolveProjectLineStoreProductId(it);
            return Number(pid) === Number(productId);
          })
          .sort((a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0));
      }

      const toRemove = candidates[0];
      if (!toRemove?.id) {
        throw new Error("No matching product item to remove.");
      }

      await deleteStoreProjectItem(projectId, toRemove.id);
      setAddedCountByProductId((prev) => ({
        ...prev,
        [productId]: Math.max(0, Number(prev?.[productId] ?? current) - 1),
      }));
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Unable to remove from project."
      );
    } finally {
      setBusyByProductId((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const handleReplaceVariantInProject = async (
    baseProduct,
    nextVariantProduct,
    folderPath
  ) => {
    // Important: each added product must remain its own project item.
    // We no longer "merge" lines by updating existing project_items to a newly selected variant.
    void baseProduct;
    void nextVariantProduct;
    void folderPath;
  };

  const handleLoadVariantCatalogProduct = async (
    folderPath,
    previousProduct,
    targetProductId
  ) => {
    const oldId = resolveStoreCatalogListProductId(previousProduct);
    const newId = toNullableNumber(targetProductId);
    if (oldId == null || newId == null || Number(oldId) === Number(newId))
      return;
    setBusyByProductId((prev) => ({ ...prev, [oldId]: true }));
    try {
      const res = await getCatalogProduct(newId);
      const data = unwrapStoreCatalogProductShow(res);
      if (!data) throw new Error("Product not found.");
      const merged = {
        ...data,
        folder_path:
          Array.isArray(previousProduct?.folder_path) &&
          previousProduct.folder_path.length > 0
            ? previousProduct.folder_path
            : folderPath,
      };
      const replace = (tree) =>
        replaceStoreCatalogProductInFoldersSmart(
          tree,
          folderPath,
          oldId,
          merged
        );
      setFolders((prev) => replace(prev));
      setSearchResultFolders((prev) => (prev != null ? replace(prev) : prev));
    } catch (e) {
      setError(
        e?.response?.data?.error || e?.message || "Unable to load variant."
      );
    } finally {
      setBusyByProductId((prev) => ({ ...prev, [oldId]: false }));
    }
  };

  if (!isLoading && !error) {
    catalogProductOrdinalRef.current = 0;
  }

  return (
    <div className="store-products">
      {!shouldHideTopIntro ? (
        <div
          className={isMobile ? "store-products__mobile-landing" : undefined}
        >
          {!isActiveCustomerUser ? (
            <div
              className="position-relative overflow-hidden d-flex align-items-center justify-content-center bg-white"
              style={{
                height: "25vh",
              }}
            >
              <div
                className="position-absolute start-0 end-0 bottom-0 opacity-25 pe-none"
                style={{
                  top: -11,
                  backgroundImage: `url(${cabinetsImg})`,
                  backgroundRepeat: "repeat",
                  backgroundPosition: "center",
                  backgroundSize: "200px center",
                }}
                aria-hidden="true"
              />
              <div
                className="position-relative px-3 py-3 text-center"
                style={{ zIndex: 1 }}
              >
                <div
                  className="fw-bold mb-2"
                  style={{ fontSize: "1.5rem", lineHeight: 1.25 }}
                >
                  <div className="fw-bold">Readymade Carcass.</div>
                  <div className="mt-1">1-Day Delivery.</div>
                </div>
                <div
                  className="text-secondary"
                  style={{ fontSize: "1rem", lineHeight: 1.25 }}
                >
                  Made for Interior Designers & Contractors
                </div>
              </div>
            </div>
          ) : null}
          {!isActiveCustomerUser ? (
            <StoreBannersCarousel
              banners={listingBanners}
              defaultBannerId={defaultBannerId}
              disableAutoplay={disableBannerAutoplay}
            />
          ) : null}
          <StoreDeliveryChargesInfo
            className="d-none"
            modalOpen={deliveryChargesModalOpen}
            onModalOpenChange={setDeliveryChargesModalOpen}
          />
        </div>
      ) : null}
      <div
        ref={searchShellStickySentinelRef}
        className="store-products__search-sticky-sentinel"
        aria-hidden
      />
      <div
        className={`store-products__search-shell${
          isSearchShellStuck ? " shadow" : ""
        }${
          showStickySampleCabinetCta
            ? " store-products__search-shell--with-sticky-cta"
            : ""
        }${
          isMobile && !showMobileSearchChrome
            ? " store-products__search-shell--mobile-collapsed"
            : ""
        }`}
      >
        {showStickySampleCabinetCta ? (
          <StoreSampleCabinetCta
            layout="sticky"
            className="mb-2"
            visible
            sampleProject={sampleCabinetProject}
            isLoggedIn={isLoggedIn}
            onAuthRequired={handleSampleCabinetAuthRequired}
            onQuoteCreated={handleSampleCabinetQuoteCreated}
            onError={(message) => setError(message)}
          />
        ) : null}
        <div className="store-products__search">
          <div className="store-products__search-pill">
            <img
              src="/store/zb_icon.png"
              alt="ZeroBuild"
              height="18"
              className="store-products__search-logo"
            />
            <input
              ref={searchInputRef}
              type="text"
              className="store-products__search-input unmask"
              placeholder="Search kitchens, wardrobes, etc."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search kitchens, wardrobes, etc."
              onFocus={() => {
                setIsSearchInputFocused(true);
                if (skipNextFocusScrollRef.current) {
                  skipNextFocusScrollRef.current = false;
                  return;
                }
              }}
              onBlur={() => setIsSearchInputFocused(false)}
            />
            {searchQuery.trim().length > 0 ? (
              <button
                type="button"
                className="btn btn-link text-secondary p-0 ms-2 text-decoration-none fs-6"
                aria-label="Clear search"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setSearchQuery("");
                  searchInputRef.current?.focus();
                }}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            ) : (
              <i className="fa-solid fa-magnifying-glass text-secondary ms-2" />
            )}
          </div>
        </div>
        {showMobileHighlightPills ? (
          <StoreHighlightFolderPills
            items={highlightFolderPillItems}
            onNavigate={(href) => navigate(href)}
          />
        ) : null}
        {showDesktopTopFoldersSkeleton ? (
          <StoreTopFoldersCarouselSkeleton items={7} />
        ) : null}
        {showDesktopTopFolders ? (
          <StoreTopFoldersCarousel
            items={topLevelCarouselItems}
            onNavigate={(href) => navigate(href)}
          />
        ) : null}
      </div>
      {isLoading ? <StoreProductsLoadingSkeleton rows={7} /> : null}
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {notice ? <div className="alert alert-success py-2">{notice}</div> : null}
      {!isLoading && !error && folders.length === 0 ? (
        <div className="store-products__empty">No folders in response.</div>
      ) : null}

      {!isLoading && !error ? (
        <div className="store-products__listing-wrap">
          {searchLoadingOverlay ? (
            <div
              className="store-products__search-loading-overlay"
              aria-busy="true"
              aria-live="polite"
            />
          ) : null}
          {!searchResolved ? (
            <StoreProductsFolderList
              folders={folders}
              isFolderExpanded={isFolderExpanded}
              onToggleFolder={toggleFolder}
              canShowPrice={canShowPrice}
              onOpenProduct={openProductDetailFromRow}
              onAddCart={handleAddCart}
              onAddProject={handleAddProject}
              onProjectIncrement={handleProjectIncrement}
              onCreateProjectAndAdd={handleCreateProjectAndAdd}
              onRemoveProduct={handleRemoveProductFromProject}
              onVariantChange={handleReplaceVariantInProject}
              onLoadVariantProduct={handleLoadVariantCatalogProduct}
              onSkuCopied={handleCopyCode}
              busyByProductId={busyByProductId}
              addedCountByProductId={addedCountByProductId}
              addedFlashByProductId={addedFlashByProductId}
              folderPath={[]}
              depth={0}
              isLoggedIn={isLoggedIn}
              isNonCartUser={isNonCartUser}
              isCustomerUser={isCustomerUser}
              isDealerUser={isDealerUser}
              isAdminUser={isAdminUser}
              currentUser={currentUser}
              projectsList={projectsList}
              onAuthRequired={openAuthModal}
              suppressQuoteDropdown={
                folderPickerModal.isOpen || createQuoteModalState.isOpen
              }
              catalogProductOrdinalRef={catalogProductOrdinalRef}
              catalogPromoBannerProps={catalogPromoBannerProps}
            />
          ) : null}
          {isSearchActive && !searchLoadingOverlay && searchFetchError ? (
            <div
              className="store-products__search-fetch-error alert alert-danger py-2 mt-2 mb-0"
              role="alert"
            >
              {searchFetchError}
            </div>
          ) : null}
          {searchResolved && searchResultFolders.length === 0 ? (
            <p className="store-products__search-empty text-muted small card m-3 p-4 text-center">
              No matching products.
            </p>
          ) : null}
          {searchResolved && searchResultFolders.length > 0 ? (
            <div className="store-products__search-results">
              <StoreProductsFolderList
                folders={searchResultFolders}
                isFolderExpanded={isFolderExpanded}
                onToggleFolder={toggleFolder}
                canShowPrice={canShowPrice}
                onOpenProduct={(product) => {
                  openProductDetailFromRow(product);
                }}
                onAddCart={handleAddCart}
                onAddProject={handleAddProject}
                onProjectIncrement={handleProjectIncrement}
                onCreateProjectAndAdd={handleCreateProjectAndAdd}
                onRemoveProduct={handleRemoveProductFromProject}
                onVariantChange={handleReplaceVariantInProject}
                onLoadVariantProduct={handleLoadVariantCatalogProduct}
                onSkuCopied={handleCopyCode}
                busyByProductId={busyByProductId}
                addedCountByProductId={addedCountByProductId}
                addedFlashByProductId={addedFlashByProductId}
                folderPath={[]}
                depth={0}
                isLoggedIn={isLoggedIn}
                isNonCartUser={isNonCartUser}
                isCustomerUser={isCustomerUser}
                isDealerUser={isDealerUser}
                isAdminUser={isAdminUser}
                currentUser={currentUser}
                projectsList={projectsList}
                onAuthRequired={openAuthModal}
                suppressQuoteDropdown={
                  folderPickerModal.isOpen || createQuoteModalState.isOpen
                }
                catalogProductOrdinalRef={catalogProductOrdinalRef}
                catalogPromoBannerProps={catalogPromoBannerProps}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      {copiedNotice ? (
        <div
          className="store-products__copy-toast bg-success"
          role="status"
          aria-live="polite"
        >
          code copied
        </div>
      ) : null}

      {!isDealerUser &&
      !isAdminUser &&
      !isLoading &&
      !error &&
      shouldShowDock ? (
        <div
          className="store-products__view-cart-dock"
          role="status"
          aria-live="polite"
        >
          <div
            className={`store-products__view-cart-card${
              isSingleSampleOrderDock
                ? " store-products__view-cart-card--sample-only"
                : ""
            }`}
          >
            {isSingleSampleOrderDock && viewQuoteHref ? (
              <Link
                to={viewQuoteHref}
                className="store-products__view-cart-link store-products__view-cart-link--sample-only"
                onClick={() => trackZbInitiateCheckout(0)}
              >
                View Sample Order
              </Link>
            ) : (
              <>
                <span className="store-products__view-cart-text">
                  {dockAddedItems} item{dockAddedItems > 1 ? "s" : ""} added
                </span>
                <div className="store-products__view-cart-actions">
                  {ownDraftQuotes.length > 0 ? (
                    <Dropdown
                      isOpen={isViewQuotePickerOpen}
                      toggle={() => setIsViewQuotePickerOpen((prev) => !prev)}
                      direction="up"
                    >
                      <DropdownToggle
                        color="link"
                        caret={false}
                        className="store-products__view-cart-link"
                      >
                        {ownDraftQuotes.length === 1
                          ? "View Quote"
                          : `${ownDraftQuotes.length} Quotes`}
                        <i className="fa-solid fa-chevron-up" aria-hidden />
                      </DropdownToggle>
                      <DropdownMenu
                        end
                        flip={false}
                        strategy="fixed"
                        container="body"
                        className="store-products__quote-dropdown-menu"
                      >
                        <StoreBuyerQuotePickerItems
                          quotes={ownDraftQuotes}
                          currentUser={currentUser}
                          placement="menu"
                          renderItem={(proj, displayName) => {
                            const pid = toNullableNumber(
                              proj?.id ?? proj?.project_id
                            );
                            if (pid == null) return null;
                            return (
                              <DropdownItem
                                key={String(pid)}
                                className="store-quote-picker-item--own"
                                tag={Link}
                                to={`/projects/${pid}`}
                                onClick={() => {
                                  setIsViewQuotePickerOpen(false);
                                  trackZbInitiateCheckout(0);
                                }}
                              >
                                {displayName}
                              </DropdownItem>
                            );
                          }}
                        />
                      </DropdownMenu>
                    </Dropdown>
                  ) : viewQuoteHref ? (
                    <Link
                      to={viewQuoteHref}
                      className="store-products__view-cart-link"
                      onClick={() => trackZbInitiateCheckout(0)}
                    >
                      View Quote
                      <i className="fa-solid fa-chevron-right" aria-hidden />
                    </Link>
                  ) : (
                    <span className="store-products__view-cart-link text-white opacity-75">
                      View Quote
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {activeCodeParam ? (
        <ProductDetailModal
          key={activeCodeParam}
          productId={urlCodeToCode(activeCodeParam)}
          isOpen
          onClose={() => {
            const from = activeProductModalState?.from;
            if (
              typeof from === "string" &&
              from.startsWith("/") &&
              !from.startsWith("//")
            ) {
              navigate(from, { replace: true });
              return;
            }
            navigate("/products", { replace: true });
          }}
          onProductIdChange={async (pid) => {
            if (pid == null) return;
            try {
              const body = await getCatalogProduct(pid);
              const root = body?.data ?? body;
              const data = root?.data ?? root;
              const nextCode = resolveStoreCatalogProductCode(data);
              if (!nextCode) return;
              navigate(
                `/products/${encodeURIComponent(codeToUrlCode(nextCode))}`,
                {
                  state: activeProductModalState || {
                    from: "/products",
                    initialAdded: 0,
                    initialHasProjects: hasProjects,
                    initialFolderPath: [],
                  },
                  replace: true,
                }
              );
            } catch (err) {
              setError(
                err?.response?.data?.error ||
                  err?.message ||
                  "Unable to open selected variant."
              );
            }
          }}
          nonCartInitialAdded={Number(
            activeProductModalState?.initialAdded ?? 0
          )}
          nonCartInitialHasProjects={
            activeProductModalState?.initialHasProjects
          }
          nonCartInitialFolderPath={
            Array.isArray(activeProductModalState?.initialFolderPath)
              ? activeProductModalState.initialFolderPath
              : []
          }
          onAddToCart={({ projectId, productId }) => {
            const pid = toNullableNumber(projectId);
            const sid = toNullableNumber(productId);
            if (pid != null) {
              setHasProjects(true);
              setAddSuccess({ projectId: pid });
              incrementProjectItemsCount(pid, 1);
            }
            if (sid != null) {
              setAddedCountByProductId((prev) => ({
                ...prev,
                [sid]: Number(prev?.[sid] ?? 0) + 1,
              }));
              showAddedToCartFlash(sid, null);
            }
          }}
        />
      ) : null}

      <StoreProjectNameModal
        isOpen={createQuoteModalState.isOpen}
        toggle={() => {
          if (createQuoteBusy) return;
          setCreateQuoteModalState({
            isOpen: false,
            product: null,
            folderPath: [],
          });
          setCreateQuoteName("");
        }}
        value={createQuoteName}
        onChange={setCreateQuoteName}
        onSubmit={submitCreateQuoteModal}
        busy={createQuoteBusy}
        submitLabel={createQuoteBusy ? "Creating..." : "Create and Add"}
      />

      <StoreQuoteFolderPickerModal
        isOpen={folderPickerModal.isOpen}
        toggle={() =>
          setFolderPickerModal({
            isOpen: false,
            projectId: null,
            projectName: "",
            product: null,
          })
        }
        projectId={folderPickerModal.projectId}
        projectName={folderPickerModal.projectName}
        product={folderPickerModal.product}
        onAdded={handleFolderPickerAdded}
        onError={(message) => setError(message)}
      />
    </div>
  );
};

function StoreProductsFolderList({
  folders,
  isFolderExpanded,
  onToggleFolder,
  canShowPrice = true,
  onOpenProduct,
  onAddCart,
  onAddProject,
  onProjectIncrement,
  onCreateProjectAndAdd,
  onRemoveProduct,
  onVariantChange,
  onLoadVariantProduct,
  onSkuCopied,
  busyByProductId,
  addedCountByProductId,
  addedFlashByProductId,
  folderPath = [],
  depth = 0,
  isLoggedIn = false,
  isNonCartUser = false,
  isCustomerUser = false,
  isDealerUser = false,
  isAdminUser = false,
  projectsList = [],
  currentUser = null,
  onAuthRequired,
  suppressQuoteDropdown = false,
  catalogProductOrdinalRef,
  catalogPromoBannerProps = {},
}) {
  if (!Array.isArray(folders) || folders.length === 0) return null;
  return (
    <>
      {folders.map((folder, index) => {
        const nodeKey = getFolderNodeKey(folderPath, folder, index);
        const products = Array.isArray(folder?.products) ? folder.products : [];
        const children = Array.isArray(folder?.children) ? folder.children : [];
        const expanded = isFolderExpanded(nodeKey);
        const count = countProductsInFolderSubtree(folder);
        const folderSqftText = canShowPrice
          ? formatSqftPrice(folder?.sqft_price)
          : null;
        const segment = String(folder?.name ?? "").trim();
        const nextFolderPath = segment ? [...folderPath, segment] : folderPath;

        return (
          <div
            key={nodeKey}
            id={folderDomId(nodeKey)}
            className="store-products-accordion"
            style={
              depth > 0 ? { marginLeft: Math.min(depth * 14, 56) } : undefined
            }
          >
            <button
              type="button"
              className="store-products-accordion__header w-100 d-flex align-items-start gap-2 border-0 bg-white text-start fw-bold"
              onClick={() => onToggleFolder(nodeKey)}
              aria-expanded={expanded}
            >
              <span className="store-products-accordion__chevron" aria-hidden>
                {expanded ? "▼" : "▶"}
              </span>
              <span className="store-products-accordion__header-title text-uppercase text-primary flex-grow-1">
                {folder?.name ?? "Folder"} ({count})
                {folderSqftText ? (
                  <span className="store-products-accordion__header-price">
                    {folderSqftText}
                  </span>
                ) : null}
              </span>
            </button>
            {expanded ? (
              <div className="store-products-accordion__body">
                {products.map((p, rowIndex) => {
                  const ordinal = catalogProductOrdinalRef
                    ? ++catalogProductOrdinalRef.current
                    : 0;
                  const showPromo =
                    !catalogPromoBannerProps.hidden &&
                    catalogProductOrdinalRef &&
                    shouldShowCatalogPromoBannerAfterProduct(ordinal);
                  const promoBannerIndex = showPromo
                    ? catalogPromoBannerIndexAfterProduct(ordinal)
                    : null;
                  return (
                    <React.Fragment
                      key={reactKeyForStoreCatalogProduct(p, rowIndex)}
                    >
                      <StoreProductRow
                        product={p}
                        onOpenProduct={onOpenProduct}
                        onAddCart={onAddCart}
                        onAddProject={onAddProject}
                        onProjectIncrement={onProjectIncrement}
                        onCreateProjectAndAdd={onCreateProjectAndAdd}
                        onRemoveProduct={onRemoveProduct}
                        onVariantChange={onVariantChange}
                        onLoadVariantProduct={onLoadVariantProduct}
                        onSkuCopied={onSkuCopied}
                        canShowPrice={canShowPrice}
                        folderPath={nextFolderPath}
                        isBusy={Boolean(
                          busyByProductId?.[resolveStoreCatalogListProductId(p)]
                        )}
                        addedCount={Number(
                          addedCountByProductId?.[
                            resolveStoreCatalogListProductId(p)
                          ] ??
                            p?.added ??
                            0
                        )}
                        isLoggedIn={isLoggedIn}
                        isNonCartUser={isNonCartUser}
                        isCustomerUser={isCustomerUser}
                        isDealerUser={isDealerUser}
                        isAdminUser={isAdminUser}
                        currentUser={currentUser}
                        projectsList={projectsList}
                        addedFlashText={
                          addedFlashByProductId?.[
                            resolveStoreCatalogListProductId(p)
                          ] || ""
                        }
                        onAuthRequired={onAuthRequired}
                        suppressQuoteDropdown={suppressQuoteDropdown}
                      />
                      {promoBannerIndex != null ? (
                        <StoreCatalogPromoBanner
                          bannerIndex={promoBannerIndex}
                          {...catalogPromoBannerProps}
                        />
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {children.length > 0 ? (
                  <StoreProductsFolderList
                    folders={children}
                    isFolderExpanded={isFolderExpanded}
                    onToggleFolder={onToggleFolder}
                    canShowPrice={canShowPrice}
                    onOpenProduct={onOpenProduct}
                    onAddCart={onAddCart}
                    onAddProject={onAddProject}
                    onProjectIncrement={onProjectIncrement}
                    onCreateProjectAndAdd={onCreateProjectAndAdd}
                    onRemoveProduct={onRemoveProduct}
                    onVariantChange={onVariantChange}
                    onLoadVariantProduct={onLoadVariantProduct}
                    onSkuCopied={onSkuCopied}
                    busyByProductId={busyByProductId}
                    addedCountByProductId={addedCountByProductId}
                    addedFlashByProductId={addedFlashByProductId}
                    folderPath={nextFolderPath}
                    depth={depth + 1}
                    isLoggedIn={isLoggedIn}
                    isNonCartUser={isNonCartUser}
                    isCustomerUser={isCustomerUser}
                    isDealerUser={isDealerUser}
                    isAdminUser={isAdminUser}
                    currentUser={currentUser}
                    projectsList={projectsList}
                    onAuthRequired={onAuthRequired}
                    suppressQuoteDropdown={suppressQuoteDropdown}
                    catalogProductOrdinalRef={catalogProductOrdinalRef}
                    catalogPromoBannerProps={catalogPromoBannerProps}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}

export default StoreProducts;
